import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import usePersistentState from '../utils/usePersistentState';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import { emailAPI, candidateAPI, requirementAPI, submissionAPI, clientAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { SearchableSelect, Button } from '../components/UI';
import DOMPurify from 'dompurify';
import { htmlToText } from '../utils/html';

// ── Constants ─────────────────────────────────────────────────────────────────
const CATEGORIES = [
  { value: 'interview_invite', label: 'Interview Invite',
    style: (d) => ({ background: d ? 'rgba(59,130,246,0.15)' : '#eff6ff', color: d ? '#93c5fd' : '#1d4ed8', border: '1px solid ' + (d ? 'rgba(59,130,246,0.3)' : '#bfdbfe') }) },
  { value: 'offer_letter', label: 'Offer Letter',
    style: (d) => ({ background: d ? 'rgba(16,185,129,0.15)' : '#ecfdf5', color: d ? '#6ee7b7' : '#047857', border: '1px solid ' + (d ? 'rgba(16,185,129,0.3)' : '#a7f3d0') }) },
  { value: 'rejection', label: 'Rejection',
    style: (d) => ({ background: d ? 'rgba(239,68,68,0.15)' : '#fef2f2', color: d ? '#fca5a5' : '#b91c1c', border: '1px solid ' + (d ? 'rgba(239,68,68,0.3)' : '#fecaca') }) },
  { value: 'followup', label: 'Follow-up',
    style: (d) => ({ background: d ? 'rgba(245,158,11,0.15)' : '#fffbeb', color: d ? '#fcd34d' : '#b45309', border: '1px solid ' + (d ? 'rgba(245,158,11,0.3)' : '#fde68a') }) },
  { value: 'job_description', label: 'Job Description',
    style: (d) => ({ background: d ? 'rgba(139,92,246,0.15)' : '#f5f3ff', color: d ? '#c4b5fd' : '#6d28d9', border: '1px solid ' + (d ? 'rgba(139,92,246,0.3)' : '#ddd6fe') }) },
  { value: 'custom', label: 'Custom',
    style: () => ({ background: 'var(--bg-raised)', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)' }) },
];

const getCategoryStyleFn = (cat) => (CATEGORIES.find(c => c.value === cat) || CATEGORIES[5]).style;
const getCategoryLabel = (cat) => CATEGORIES.find(c => c.value === cat)?.label || 'Custom';

// -- Experience formatter --
const fmtExp = (v) => {
  if (v == null || v === '') return '';
  const total = Math.round(Number(v) * 12);
  const yrs = Math.floor(total / 12);
  const mos = total % 12;
  if (yrs > 0 && mos > 0) return `${yrs} yr${yrs !== 1 ? 's' : ''} ${mos} month${mos !== 1 ? 's' : ''}`;
  if (yrs > 0) return `${yrs} yr${yrs !== 1 ? 's' : ''}`;
  return `${mos} month${mos !== 1 ? 's' : ''}`;
};

// Relevant experience is a free-text field (e.g. "React 5yr, Redux 3yr") but is
// frequently entered as a bare number of years. Show a bare number as
// "X yr Y month" like total experience; leave real text untouched so we never
// print "NaN months".
const fmtRelevantExp = (v) => {
  if (v == null || String(v).trim() === '') return '';
  const raw = String(v).trim();
  return /^\d+(?:\.\d+)?$/.test(raw) ? (fmtExp(raw) || raw) : raw;
};

// ── Retired-field degradation ────────────────────────────────────────────────
// The numeric noticePeriod / relevantExperience inputs were removed from the
// candidate form; those details are now typed once into the rich-text Notice
// Period Notes / Relevant Experience Summary. Templates still reference
// {{notice_period}} and {{relevant_experience}}, so both fall back in order:
// legacy stored value → the serving flag (notice only) → the rich-text field,
// flattened to plain text because these land in inline contexts (table cells,
// single-line rows) where block HTML would break the layout.
// Defined once so the single-send, batch-loop and legacy-table call sites can
// never drift apart.

// `short` is the compact label used in the narrow batch-table cell.
const noticeText = (c, { short = false } = {}) => {
  if (c?.noticePeriod != null) return c.noticePeriod === 0 ? 'Immediate' : `${c.noticePeriod} days`;
  if (c?.servingNoticePeriod) return short ? 'Serving' : 'Serving notice period';
  return htmlToText(c?.noticePeriodNotes);
};

const relevantExpText = (c) =>
  c?.relevantExperience ? fmtRelevantExp(c.relevantExperience) : htmlToText(c?.experienceSummary);

// Convert plain-text body (with \n line breaks) to HTML for contentEditable rendering.
// If body already contains HTML tags, return as-is.
const bodyToHtml = (body) => {
  if (!body) return '';
  if (/<[a-z][\s\S]*>/i.test(body)) return body; // already HTML
  return body
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>');
};

const TABS = ['Templates', 'Compose', 'Sent Logs'];

// ── Requirement → template variable mapper ────────────────────────────────────
// Converts a requirement object into every template variable it can supply.
// Add new mappings here when new template variables are introduced.
function buildReqVars(req) {
  if (!req) return {};

  const skills     = Array.isArray(req.requiredSkills) ? req.requiredSkills : [];
  const skillsCsv  = skills.join(', ');
  const skillsHtml = skills.map(s => `<li>${s}</li>`).join('');

  const expRange = (() => {
    const mn = req.minExperience, mx = req.maxExperience;
    if (mn != null && mx != null) return `${mn}–${mx} yrs`;
    if (mn != null) return `${mn}+ yrs`;
    if (mx != null) return `Up to ${mx} yrs`;
    return '';
  })();

  const salaryRange = (() => {
    const mn = req.budgetMin, mx = req.budgetMax, cur = req.budgetCurrency || '';
    if (mn && mx) return `${cur} ${mn}–${mx}`.trim();
    if (mn)       return `${cur} ${mn}+`.trim();
    return '';
  })();

  return {
    // Job identity
    job_title:            req.jobTitle             || '',
    job_type:             req.jobType              || '',
    work_mode:            req.workMode             || '',
    status:               req.status               || '',
    priority:             req.priority             || '',
    openings:             req.openings             || '',
    // Location
    location:             req.location             || '',
    job_location:         req.jobLocation || req.location || '',
    // Experience
    min_experience:       req.minExperience        ?? '',
    max_experience:       req.maxExperience        ?? '',
    experience:           expRange,
    experience_range:     expRange,
    // Budget / salary
    budget_min:           req.budgetMin            || '',
    budget_max:           req.budgetMax            || '',
    budget_currency:      req.budgetCurrency       || '',
    salary_range:         salaryRange,
    // Billing
    billing_cost:         req.billingCost          || '',
    billing_type:         req.billingType          || '',
    billing_currency:     req.billingCurrency      || '',
    // Skills
    required_skills:      skillsCsv,
    required_skills_html: skillsHtml,
    skills:               skillsCsv,
    skills_html:          skillsHtml,
    // Descriptions
    job_description:      req.jobDescription       || '',
    description:          req.description          || '',
    // Dates
    close_by:             req.closeBy              || '',
    // Company (from association)
    company_name:         req.client?.clientName   || '',
  };
}

// Variables that the backend/system fills automatically — do NOT ask the user for these
const AUTO_FILLED_VARS = new Set([
  // Candidate fields
  'candidate_name', 'candidate_email', 'candidate_phone', 'candidate_location',
  'candidate_experience', 'candidate_skills', 'notice_period', 'offer_in_hand',
  'relevant_experience', 'total_experience',
  // Sender / misc
  'sender_name', 'candidates_table', 'client_name', 'client_email',
  // All requirement-derived vars (keys from buildReqVars)
  ...Object.keys(buildReqVars({})),
]);

// Extract variables from a template that are NOT auto-filled (user must supply
// them). Variables INSIDE a {{#candidates}} loop block are per-candidate fields
// filled by the engine, so we strip those blocks first before scanning.
function getRequiredVars(template) {
  if (!template) return [];
  const stripLoops = (s) => String(s || '').replace(/\{\{#candidates\}\}[\s\S]*?\{\{\/candidates\}\}/g, '');
  const text = `${template.subject || ''} ${stripLoops(template.body || '')}`;
  const found = [...new Set([...text.matchAll(/\{\{(\w+)\}\}/g)].map(m => m[1]))];
  return found.filter(v => !AUTO_FILLED_VARS.has(v));
}

// Human-friendly label from a snake_case variable name
const varLabel = (v) => v.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const AUDIENCES = [
  { value: 'candidate', label: 'Candidate', icon: '👤',
    style: (d) => ({ background: d ? 'rgba(99,102,241,0.15)' : '#eef2ff', color: d ? '#a5b4fc' : '#4338ca', border: '1px solid ' + (d ? 'rgba(99,102,241,0.3)' : '#c7d2fe') }) },
  { value: 'client', label: 'Client', icon: '🏢',
    style: (d) => ({ background: d ? 'rgba(16,185,129,0.15)' : '#ecfdf5', color: d ? '#6ee7b7' : '#047857', border: '1px solid ' + (d ? 'rgba(16,185,129,0.3)' : '#a7f3d0') }) },
  { value: 'both', label: 'Both', icon: '⚡',
    style: () => ({ background: 'var(--bg-raised)', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)' }) },
];
const getAudienceInfo = (aud) => AUDIENCES.find(a => a.value === aud) || AUDIENCES[2];
const AudienceBadge = ({ audience }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const info = getAudienceInfo(audience);
  return (
    <span style={{ ...info.style(isDark), fontSize: 11, padding: '2px 8px', borderRadius: 9999, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {info.icon} {info.label}
    </span>
  );
};

// ── Email recipient tag-input (To / Cc) ───────────────────────────────────────
function EmailTagInput({ label, emails, onChange, isDark }) {
  const [inputVal, setInputVal] = useState('');

  const addEmail = (raw) => {
    const email = raw.trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error(`"${email}" is not a valid email address`);
      return;
    }
    if (!emails.includes(email)) onChange([...emails, email]);
    setInputVal('');
  };

  const removeEmail = (email) => onChange(emails.filter(e => e !== email));

  const handleKeyDown = (e) => {
    if (['Enter', ',', 'Tab'].includes(e.key)) {
      e.preventDefault();
      addEmail(inputVal);
    } else if (e.key === 'Backspace' && !inputVal && emails.length > 0) {
      removeEmail(emails[emails.length - 1]);
    }
  };

  return (
    <div style={{
      borderBottom: '1px solid var(--border-subtle)',
      display: 'flex', alignItems: 'flex-start', gap: 8,
      padding: '6px 0', minHeight: 36,
    }}>
      <span style={{
        color: 'var(--text-muted)', fontSize: 12, fontWeight: 500,
        width: 28, paddingTop: 5, flexShrink: 0,
      }}>{label}</span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, flex: 1, alignItems: 'center', minHeight: 28 }}>
        {emails.map(email => (
          <span key={email} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: isDark ? 'rgba(99,102,241,0.12)' : '#f0f4ff',
            border: `1px solid ${isDark ? 'rgba(99,102,241,0.3)' : '#c7d2fe'}`,
            borderRadius: 20, padding: '2px 8px 2px 6px', fontSize: 12,
            color: isDark ? '#a5b4fc' : '#4338ca',
          }}>
            <span style={{ color: '#22c55e', fontSize: 9, lineHeight: 1 }}>●</span>
            {email}
            <button onClick={() => removeEmail(email)} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: isDark ? '#6b7280' : '#9ca3af', padding: 0,
              lineHeight: 1, fontSize: 15, display: 'flex', alignItems: 'center', marginLeft: 1,
            }}>×</button>
          </span>
        ))}
        <input
          value={inputVal}
          onChange={e => setInputVal(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => { if (inputVal) addEmail(inputVal); }}
          placeholder={emails.length === 0 ? 'Add email…' : ''}
          style={{
            background: 'transparent', border: 'none', outline: 'none',
            color: 'var(--text-primary)', fontSize: 12,
            minWidth: 100, flex: 1, padding: '2px 0',
          }}
        />
      </div>
    </div>
  );
}

// ── Reusable components ───────────────────────────────────────────────────────
const Badge = ({ category }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <span style={{ ...getCategoryStyleFn(category)(isDark), fontSize: 11, padding: '2px 8px', borderRadius: 9999, fontWeight: 500, display: 'inline-block' }}>
      {getCategoryLabel(category)}
    </span>
  );
};

// ── BCC preview helpers ───────────────────────────────────────────────────────
// Read-only mirror of the backend BCC resolution, used ONLY to show the operator
// which addresses will be BCC'd (and from which source). The backend remains the
// source of truth for what is actually sent.
const BCC_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const parseBccList = (input) => {
  const raw = Array.isArray(input) ? input : (input == null ? [] : String(input).split(','));
  const seen = new Set();
  const out = [];
  for (const item of raw) {
    const email = String(item ?? '').trim();
    if (!email || !BCC_EMAIL_RE.test(email)) continue;
    const key = email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(email);
  }
  return out;
};
// Requirement contact person email(s) to auto-BCC: ONLY contacts whose per-contact
// `bcc` toggle is on (mirrors the backend helper). Others are excluded.
const reqContactEmails = (requirement) => {
  if (!requirement) return [];
  const contacts = Array.isArray(requirement.contacts) ? requirement.contacts : [];
  return contacts.filter(c => c && c.bcc === true && c.email).map(c => c.email);
};

// A "batch" template renders multiple candidates — either via the classic
// horizontal {{candidates_table}} variable, or via a template-defined
// {{#candidates}}…{{/candidates}} loop block (any layout).
const hasBatchTableVar = (t) => {
  if (!t) return false;
  const body = t.body || '';
  const hasTable = Array.isArray(t.variables) ? t.variables.includes('candidates_table') : body.includes('{{candidates_table}}');
  return hasTable || body.includes('{{#candidates}}');
};

const Modal = ({ title, onClose, children }) => {
  const titleId = React.useId();

  // Close on Escape. (Background scroll is already locked by the page's
  // `html.scroll-locked` effect while any overlay is open.)
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Portal to <body> so the fixed overlay is anchored to the VIEWPORT, not to a
  // transformed/animated page ancestor. A `position:fixed` element nested inside
  // an ancestor with `transform`/`filter` uses that ancestor as its containing
  // block (per CSS spec) — so it behaves like `absolute` and drifts off-screen
  // as the page scrolls. Rendering into document.body escapes that entirely.
  // maxHeight:90vh + the min-height:0 scroll body keep long templates fully
  // visible and scrollable no matter where the modal is opened from.
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <div
        style={{
          background: 'var(--bg-card)', border: '1px solid var(--border-subtle)',
          maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        }}
        className="rounded-xl w-full max-w-2xl shadow-2xl">
        <div style={{ borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }} className="flex items-center justify-between gap-2 px-4 sm:px-6 py-3 sm:py-4">
          <h2 id={titleId} style={{ color: 'var(--text-primary)' }} className="font-semibold truncate min-w-0">{title}</h2>
          <Button variant="ghost" size="sm" iconOnly onClick={onClose} aria-label="Close" style={{ fontSize: 18, flexShrink: 0 }}>×</Button>
        </div>
        <div style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }} className="px-6 py-4">{children}</div>
      </div>
    </div>,
    document.body,
  );
};

// Colorful template picker — groups templates by category with color-coded badges
const TemplatePicker = ({ label, value, onChange, templates, required = false }) => {
  const [open, setOpen] = React.useState(false);
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const ref = React.useRef(null);
  const selected = templates.find(t => t.id === value);
  const selCat = selected ? CATEGORIES.find(c => c.value === selected.category) : null;

  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const grouped = CATEGORIES.map(cat => ({
    ...cat,
    items: templates.filter(t => t.category === cat.value),
  })).filter(g => g.items.length > 0);

  const CAT_ICONS = {
    interview_invite: '🎯', offer_letter: '🎉', rejection: '❌',
    followup: '🔄', job_description: '📋', custom: '✏️',
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {label && <label style={{ color: 'var(--text-muted)', fontSize: 12, display: 'block', marginBottom: 4 }}>{label}</label>}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 12px', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
          background: isDark ? 'var(--bg-input)' : '#ffffff',
          border: `1px solid ${open ? (isDark ? 'rgba(99,102,241,0.7)' : '#6366f1') : (isDark ? 'rgba(255,255,255,0.15)' : '#cbd5e1')}`,
          color: 'var(--text-primary)', fontSize: 14, transition: 'border-color 0.15s',
          boxShadow: open ? `0 0 0 3px ${isDark ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.1)'}` : 'none',
          minHeight: 38,
        }}
      >
        {selected ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, fontWeight: 600, ...(selCat?.style(isDark) || {}) }}>
              {CAT_ICONS[selected.category] || '✉️'} {selCat?.label || 'Custom'}
            </span>
            <span style={{ fontWeight: 500 }}>{selected.name}</span>
          </span>
        ) : (
          <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>— Select a template{required ? ' *' : ''} —</span>
        )}
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginLeft: 8, color: 'var(--text-muted)', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
          <path d="M5 8l5 5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 9999,
          background: 'var(--bg-card)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
          borderRadius: 12, boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.5)' : '0 8px 32px rgba(0,0,0,0.12)',
          maxHeight: 320, overflowY: 'auto', padding: 8,
        }}>
          {/* Clear option */}
          <div
            onClick={() => { onChange(''); setOpen(false); }}
            style={{
              padding: '7px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 12,
              color: 'var(--text-muted)', marginBottom: 4,
              background: !value ? (isDark ? 'rgba(99,102,241,0.1)' : '#eef2ff') : 'transparent',
            }}
            onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc'}
            onMouseLeave={e => e.currentTarget.style.background = !value ? (isDark ? 'rgba(99,102,241,0.1)' : '#eef2ff') : 'transparent'}
          >
            — No template —
          </div>

          {grouped.map(group => (
            <div key={group.value} style={{ marginBottom: 4 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', marginBottom: 2,
              }}>
                <span style={{ fontSize: 10, padding: '1px 8px', borderRadius: 99, fontWeight: 700, letterSpacing: 0.4, ...group.style(isDark) }}>
                  {CAT_ICONS[group.value]} {group.label}
                </span>
              </div>
              {group.items.map(t => (
                <div
                  key={t.id}
                  onClick={() => { onChange(t.id); setOpen(false); }}
                  style={{
                    padding: '7px 10px 7px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
                    fontWeight: value === t.id ? 600 : 400,
                    color: value === t.id ? (isDark ? '#a5b4fc' : '#4f46e5') : 'var(--text-primary)',
                    background: value === t.id ? (isDark ? 'rgba(99,102,241,0.12)' : '#eef2ff') : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}
                  onMouseEnter={e => { if (value !== t.id) e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc'; }}
                  onMouseLeave={e => { if (value !== t.id) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span>{t.name}</span>
                  {value === t.id && <span style={{ fontSize: 11, color: isDark ? '#a5b4fc' : '#6366f1' }}>✓</span>}
                </div>
              ))}
            </div>
          ))}

          {templates.length === 0 && (
            <p style={{ fontSize: 12, color: 'var(--text-muted)', padding: '8px 10px' }}>No templates found.</p>
          )}
        </div>
      )}
    </div>
  );
};

const TextField = ({ label, value, onChange, placeholder }) => (
  <div>
    <label style={{ color: 'var(--text-muted)', fontSize: 12, display: 'block', marginBottom: 4 }}>{label}</label>
    <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      style={{ background: 'var(--bg-input)', border: '1px solid var(--border-input)', color: 'var(--text-primary)' }}
      className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none" />
  </div>
);

const InfoCard = ({ icon, title, rows }) => (
  <div style={{ background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: 12 }} className="space-y-1.5">
    <p style={{ color: 'var(--text-secondary)', fontSize: 12, fontWeight: 500, marginBottom: 8 }}>{icon} {title}</p>
    {rows.filter(([, v]) => v).map(([label, value]) => (
      <div key={label} className="flex gap-2 text-xs">
        <span style={{ color: 'var(--text-muted)', width: 96, flexShrink: 0 }}>{label}:</span>
        <span style={{ color: 'var(--text-secondary)' }} className="truncate">{value}</span>
      </div>
    ))}
  </div>
);

// ── Template Form ─────────────────────────────────────────────────────────────
function TemplateForm({ initial, onSave, onClose, canManageClients }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [form, setForm] = useState({
    name:           initial?.name           || '',
    category:       initial?.category       || 'custom',
    targetAudience: initial?.targetAudience || 'both',
    subject:        initial?.subject        || '',
    bcc:            initial?.bcc            || '',
    body:           initial?.body           || '',
  });
  const [saving, setSaving] = useState(false);

  const detectedVars = [...new Set(
    [...`${form.subject} ${form.body}`.matchAll(/\{\{(\w+)\}\}/g)].map(m => m[1])
  )];

  const handleSubmit = async () => {
    if (!form.name || !form.subject || !form.body)
      return toast.error('Name, subject and body are required.');
    setSaving(true);
    try {
      if (initial?.id) {
        await emailAPI.updateTemplate(initial.id, form);
        toast.success('Template updated.');
      } else {
        await emailAPI.createTemplate(form);
        toast.success('Template created.');
      }
      onSave();
    } catch { toast.error('Failed to save template.'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label style={{ color: 'var(--text-muted)', fontSize: 12, display: 'block', marginBottom: 4 }}>Template name</label>
          <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
            style={{ background: 'var(--bg-input)', border: '1px solid var(--border-input)', color: 'var(--text-primary)' }}
            className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none"
            placeholder="e.g. Interview – Tech Roles" />
        </div>
        <div>
          <label style={{ color: 'var(--text-muted)', fontSize: 12, display: 'block', marginBottom: 4 }}>Category</label>
          <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
            style={{ background: 'var(--bg-input)', border: '1px solid var(--border-input)', color: 'var(--text-primary)' }}
            className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none">
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
      </div>

      {/* JD template hint */}
      {form.category === 'job_description' && (
        <div style={{
          padding: '10px 14px', borderRadius: 8, fontSize: 12, lineHeight: 1.6,
          background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.25)',
          color: 'var(--text-secondary)',
        }}>
          <strong style={{ color: '#8b5cf6' }}>📌 Job Description templates</strong> are sent directly to the <strong>candidate</strong> when you add a new candidate and check "Send JD Email". Make sure this template is addressed to the candidate (use <code>{'{{candidate_name}}'}</code>), not to a client. Set "Send to" → <strong>Candidate</strong> below.
        </div>
      )}

      {/* Target audience — controls which compose tab shows this template */}
      <div>
        <label style={{ color: 'var(--text-muted)', fontSize: 12, display: 'block', marginBottom: 8 }}>Send to *</label>
        <div className="flex gap-2 flex-wrap">
          {AUDIENCES.filter(a => canManageClients || a.value === 'candidate').map(a => (
            <button key={a.value} type="button"
              onClick={() => setForm(p => ({ ...p, targetAudience: a.value }))}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                form.targetAudience === a.value
                  ? a.value === 'candidate' ? 'bg-indigo-600 border-indigo-500 text-white shadow'
                    : a.value === 'client'  ? 'bg-emerald-600 border-emerald-500 text-white shadow'
                    : 'bg-slate-600 border-slate-500 text-white shadow'
                  : 'border-slate-600/50 bg-transparent text-white'
              }`}>
              <span>{a.icon}</span> {a.label}
            </button>
          ))}
        </div>
        <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 6 }}>
          {form.targetAudience === 'candidate' && 'This template will only appear in the "Send to Candidate" tab.'}
          {form.targetAudience === 'client'    && 'This template will only appear in the "Send to Client" tab.'}
          {form.targetAudience === 'both'      && 'This template will appear in both compose tabs.'}
        </p>
      </div>
      <div>
        <label style={{ color: 'var(--text-muted)', fontSize: 12, display: 'block', marginBottom: 4 }}>Subject</label>
        <input value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
          style={{ background: 'var(--bg-input)', border: '1px solid var(--border-input)', color: 'var(--text-primary)' }}
          className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none"
          placeholder="Use {{variable}} for dynamic values" />
      </div>
      <div>
        <label style={{ color: 'var(--text-muted)', fontSize: 12, display: 'block', marginBottom: 4 }}>BCC (optional)</label>
        {/* Chip / tag input — same UX as the compose To/Cc fields. Type an address
            and press Enter or comma to add it; click × to remove. Stored as a
            comma-separated string, so we split on read and join on change. */}
        <EmailTagInput
          label="Bcc"
          emails={parseBccList(form.bcc)}
          onChange={arr => setForm(p => ({ ...p, bcc: arr.join(', ') }))}
          isDark={isDark}
        />
        <p style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 4 }}>
          Add one or more addresses (press Enter or comma to confirm each). Always BCC'd on emails from this
          template. For candidate emails the requirement's toggled contacts are added automatically.
        </p>
      </div>
      <div>
        <label style={{ color: 'var(--text-muted)', fontSize: 12, display: 'block', marginBottom: 4 }}>Body</label>
        <textarea value={form.body} onChange={e => setForm(p => ({ ...p, body: e.target.value }))} rows={12}
          style={{ background: 'var(--bg-input)', border: '1px solid var(--border-input)', color: 'var(--text-primary)' }}
          className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none font-mono resize-none"
          placeholder="Write your email body here. Use {{candidate_name}}, {{job_title}}, etc." />
      </div>
      {detectedVars.length > 0 && (
        <div style={{ background: 'var(--bg-raised)', borderRadius: 8, padding: 12 }}>
          <p style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 8 }}>Detected variables</p>
          <div className="flex flex-wrap gap-1.5">
            {detectedVars.map(v => (
              <span key={v} style={{ background: isDark ? 'rgba(99,102,241,0.15)' : '#eef2ff', color: isDark ? '#a5b4fc' : '#4338ca', border: '1px solid ' + (isDark ? 'rgba(99,102,241,0.3)' : '#c7d2fe'), fontSize: 11, padding: '2px 6px', borderRadius: 4, fontFamily: 'monospace' }}>{`{{${v}}}`}</span>
            ))}
          </div>
        </div>
      )}
      <div className="flex justify-end gap-3 pt-3 sticky bottom-0 -mx-6 px-6 pb-1"
        style={{ background: 'var(--bg-card)', borderTop: '1px solid var(--border-subtle)' }}>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} loading={saving} loadingText="Saving...">{initial?.id ? 'Update template' : 'Create template'}</Button>
      </div>
    </div>
  );
}

// ── Templates Tab ─────────────────────────────────────────────────────────────
function TemplatesTab() {
  const { isSuperAdmin, isRecruiter, isRecruiterManager } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const canEdit   = isSuperAdmin() || isRecruiterManager(); // create + edit
  const canDelete = isSuperAdmin();                         // delete only
  const canManageClients = !isRecruiter();                  // audience = client/both

  const [templates, setTemplates]       = useState([]);
  const [loading, setLoading]           = useState(true);
  // Kept for this tab — survives leaving the page and coming back.
  const [search, setSearch]             = usePersistentState('emails:templates:search', '');
  const [filterCat, setFilterCat]       = usePersistentState('emails:templates:category', '');
  const [filterAud, setFilterAud]       = usePersistentState('emails:templates:audience', '');
  const [showForm, setShowForm]         = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [viewTemplate, setViewTemplate] = useState(null);
  const [deleting, setDeleting]         = useState(null);
  const [toggling, setToggling]         = useState(null);
  const [duplicating, setDuplicating]   = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Batch-submission template = client-facing (or both) AND renders multiple
  // candidates ({{candidates_table}} or a {{#candidates}} loop). Only these get
  // the Duplicate / Hide buttons.
  const isBatchTemplate = (t) =>
    (t.targetAudience === 'client' || t.targetAudience === 'both') && hasBatchTableVar(t);

  // Duplicate a template, giving the copy the next available " N" suffix so the
  // manager ends up with "… 2", "… 3", etc.
  const handleDuplicate = async (t) => {
    setDuplicating(t.id);
    try {
      // Strip an existing trailing " <number>" to find the base name, then pick
      // the next number not already used by another template sharing that base.
      const base = t.name.replace(/\s+\d+\s*$/, '').trim();
      const used = new Set(
        templates
          .map(tpl => {
            const m = tpl.name.match(/^(.*?)\s+(\d+)\s*$/);
            if (m && m[1].trim() === base) return Number(m[2]);
            return tpl.name.trim() === base ? 1 : null;
          })
          .filter(n => n != null)
      );
      let n = 2;
      while (used.has(n)) n += 1;
      const { data } = await emailAPI.createTemplate({
        name:           `${base} ${n}`,
        category:       t.category,
        targetAudience: t.targetAudience,
        subject:        t.subject,
        body:           t.body,
        bcc:            t.bcc || '',
      });
      // Optimistically add the new template so a follow-up Duplicate computes the
      // next number correctly even before the re-fetch lands. load() then syncs
      // the authoritative copy (with associations) from the now-busted cache.
      if (data?.template) setTemplates(prev => [data.template, ...prev]);
      toast.success(`Duplicated as "${base} ${n}".`);
      load();
    } catch { toast.error('Failed to duplicate template.'); }
    finally { setDuplicating(null); }
  };

  // Toggle whether a batch template is hidden from the Batch-to-Client dropdown.
  // Optimistic flip with revert on failure (updateTemplate busts the cache).
  const handleToggleBatchVisibility = async (t) => {
    const next = !t.hideFromBatch;
    setTemplates(prev => prev.map(tpl => (tpl.id === t.id ? { ...tpl, hideFromBatch: next } : tpl)));
    try {
      await emailAPI.updateTemplate(t.id, { hideFromBatch: next });
      toast.success(next ? 'Hidden from Batch-to-Client.' : 'Shown in Batch-to-Client.');
    } catch {
      toast.error('Failed to update visibility.');
      setTemplates(prev => prev.map(tpl => (tpl.id === t.id ? { ...tpl, hideFromBatch: !next } : tpl)));
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // showAll=true → returns active + disabled templates for the manager view
      const { data } = await emailAPI.getTemplates({ search, category: filterCat, showAll: true });
      const filtered = filterAud
        ? data.templates.filter(t => t.targetAudience === filterAud || t.targetAudience === 'both')
        : data.templates;
      setTemplates(filtered);
    } catch { toast.error('Failed to load templates.'); }
    finally { setLoading(false); }
  }, [search, filterCat, filterAud]);

  const handleToggle = async (t) => {
    setToggling(t.id);
    try {
      const { data } = await emailAPI.toggleTemplate(t.id);
      toast.success(data.message);
      // Optimistic update — flip isActive immediately without re-fetching
      setTemplates(prev => prev.map(tmpl =>
        tmpl.id === t.id ? { ...tmpl, isActive: data.isActive } : tmpl
      ));
    } catch { toast.error('Failed to update template.'); }
    finally { setToggling(null); }
  };

  useEffect(() => { load(); }, [load]);

  const handleDelete = (id) => { setConfirmDelete(id); };

  const handleDeleteConfirmed = async () => {
    const id = confirmDelete;
    setConfirmDelete(null);
    setDeleting(id);
    try { await emailAPI.deleteTemplate(id); toast.success('Template deleted.'); load(); }
    catch { toast.error('Failed to delete.'); }
    finally { setDeleting(null); }
  };

  const anyOverlayOpen = showForm || !!viewTemplate || !!confirmDelete;
  useLayoutEffect(() => {
    if (anyOverlayOpen) {
      document.documentElement.classList.add('scroll-locked');
      return () => { document.documentElement.classList.remove('scroll-locked'); };
    }
  }, [anyOverlayOpen]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search templates..."
          style={{ background: 'var(--bg-input)', border: '1px solid var(--border-input)', color: 'var(--text-primary)' }}
          className="flex-1 rounded-lg px-3 py-2 text-sm focus:outline-none" />
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
          style={{ background: 'var(--bg-input)', border: '1px solid var(--border-input)', color: 'var(--text-primary)', minWidth: 140 }}
          className="rounded-lg px-3 py-2 text-sm focus:outline-none">
          <option value="">All categories</option>
          {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        {/* Recruiters only ever see candidate templates — hide Client/Both filter options */}
        <select value={filterAud} onChange={e => setFilterAud(e.target.value)}
          style={{ background: 'var(--bg-input)', border: '1px solid var(--border-input)', color: 'var(--text-primary)', minWidth: 140 }}
          className="rounded-lg px-3 py-2 text-sm focus:outline-none">
          <option value="">All audiences</option>
          {AUDIENCES.filter(a => canManageClients || a.value === 'candidate').map(a => (
            <option key={a.value} value={a.value}>{a.icon} {a.label}</option>
          ))}
        </select>
        {/* Only managers can create templates */}
        {canEdit && (
          <Button variant="primary" size="sm" onClick={() => { setEditTemplate(null); setShowForm(true); }} style={{ flexShrink: 0 }}>+ New template</Button>
        )}
      </div>
      {loading ? <div style={{ color: 'var(--text-muted)' }} className="text-center py-16 text-sm">Loading...</div>
        : templates.length === 0 ? <div style={{ color: 'var(--text-muted)' }} className="text-center py-16 text-sm">No templates found.</div>
        : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {templates.map(t => (
              <div key={t.id} style={{
                background: 'var(--bg-card)',
                border: `1px solid ${t.isActive ? 'var(--border-subtle)' : isDark ? 'rgba(239,68,68,0.25)' : '#fecaca'}`,
                opacity: t.isActive ? 1 : 0.7,
              }} className="rounded-xl p-4 flex flex-col gap-3 transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p style={{ color: t.isActive ? 'var(--text-primary)' : 'var(--text-muted)' }} className="font-medium text-sm truncate">{t.name}</p>
                      {!t.isActive && (
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 99, background: isDark ? 'rgba(239,68,68,0.15)' : '#fee2e2', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)', flexShrink: 0 }}>Disabled</span>
                      )}
                    </div>
                    <p style={{ color: 'var(--text-secondary)' }} className="text-xs mt-0.5 truncate">{t.subject}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                    <Badge category={t.category} />
                    <AudienceBadge audience={t.targetAudience || 'both'} />
                  </div>
                </div>
                <p style={{ color: 'var(--text-muted)' }} className="text-xs line-clamp-2 leading-relaxed">{t.body}</p>
                {t.variables?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {t.variables.slice(0, 4).map(v => (
                      <span key={v} style={{ background: 'var(--bg-raised)', color: 'var(--text-secondary)', fontSize: 11, padding: '2px 6px', borderRadius: 4, fontFamily: 'monospace' }}>{`{{${v}}}`}</span>
                    ))}
                    {t.variables.length > 4 && <span style={{ color: 'var(--text-muted)' }} className="text-xs">+{t.variables.length - 4} more</span>}
                  </div>
                )}
                <div style={{ borderTop: '1px solid var(--border-subtle)' }} className="flex flex-wrap gap-2 pt-1">
                  <Button variant="ghost" size="xs" onClick={() => setViewTemplate(t)} style={{ flex: 1, minWidth: 64 }}>View</Button>
                  {canEdit && (
                    <Button variant="outline" size="xs" onClick={() => { setEditTemplate(t); setShowForm(true); }} style={{ flex: 1, minWidth: 64 }}>Edit</Button>
                  )}
                  {canEdit && isBatchTemplate(t) && (
                    <Button variant="outline" size="xs" loading={duplicating === t.id} loadingText="…" onClick={() => handleDuplicate(t)} style={{ flex: 1, minWidth: 64 }} tooltip="Create a copy of this batch format">Duplicate</Button>
                  )}
                  {canEdit && isBatchTemplate(t) && (
                    <Button variant="outline" size="xs" onClick={() => handleToggleBatchVisibility(t)} style={{ flex: 1, minWidth: 64, color: t.hideFromBatch ? '#6b7280' : 'var(--text-primary)' }} tooltip={t.hideFromBatch ? 'Hidden from the Batch-to-Client dropdown — click to show' : 'Hide from the Batch-to-Client dropdown'}>{t.hideFromBatch ? '🙈 Hidden' : '👁 Hide'}</Button>
                  )}
                  {canDelete && (
                    <Button
                      size="xs"
                      loading={toggling === t.id}
                      loadingText="…"
                      onClick={() => handleToggle(t)}
                      style={{
                        flex: 1,
                        background: t.isActive ? (isDark ? 'rgba(245,158,11,0.15)' : '#fef3c7') : (isDark ? 'rgba(16,185,129,0.15)' : '#d1fae5'),
                        color: t.isActive ? '#d97706' : '#059669',
                        border: `1px solid ${t.isActive ? 'rgba(245,158,11,0.4)' : 'rgba(16,185,129,0.4)'}`,
                      }}
                    >
                      {t.isActive ? 'Disable' : 'Enable'}
                    </Button>
                  )}
                  {canDelete && (
                    <Button variant="danger" size="xs" loading={deleting === t.id} loadingText="Deleting…" onClick={() => handleDelete(t.id)} style={{ flex: 1 }}>Delete</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      {showForm && (
        <Modal title={editTemplate ? 'Edit template' : 'New template'} onClose={() => setShowForm(false)}>
          <TemplateForm initial={editTemplate} canManageClients={canManageClients}
            onSave={() => { setShowForm(false); load(); }} onClose={() => setShowForm(false)} />
        </Modal>
      )}
      {viewTemplate && (
        <Modal title={viewTemplate.name} onClose={() => setViewTemplate(null)}>
          <div className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Badge category={viewTemplate.category} />
              <AudienceBadge audience={viewTemplate.targetAudience || 'both'} />
              <span style={{ color: 'var(--text-muted)' }} className="text-xs">by {viewTemplate.createdByUser?.name}</span>
            </div>
            <div><p style={{ color: 'var(--text-muted)' }} className="text-xs mb-1">Subject</p><p style={{ color: 'var(--text-primary)', background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)' }} className="text-sm rounded-lg px-3 py-2">{viewTemplate.subject}</p></div>
            <div><p style={{ color: 'var(--text-muted)' }} className="text-xs mb-1">Body</p>
              <pre style={{ color: 'var(--text-primary)', background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)' }} className="text-sm rounded-lg px-3 py-3 whitespace-pre-wrap font-sans leading-relaxed">{viewTemplate.body}</pre>
            </div>
            {viewTemplate.variables?.length > 0 && (
              <div>
                <p style={{ color: 'var(--text-muted)' }} className="text-xs mb-2">Variables</p>
                <div className="flex flex-wrap gap-1.5">
                  {viewTemplate.variables.map(v => (
                    <span key={v} style={{ background: 'var(--bg-raised)', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)', fontSize: 11, padding: '2px 8px', borderRadius: 4, fontFamily: 'monospace' }}>{`{{${v}}}`}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}
      {confirmDelete && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)' }} onClick={() => setConfirmDelete(null)} />
          <div style={{ position: 'relative', background: isDark ? '#1e2535' : '#ffffff', borderRadius: 16, padding: '28px', width: '100%', maxWidth: 400, boxShadow: '0 24px 64px rgba(0,0,0,0.4)' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 700, color: isDark ? '#f1f5f9' : '#111827' }}>Delete Template?</h3>
            <p style={{ margin: '0 0 24px', fontSize: 14, color: isDark ? '#94a3b8' : '#6b7280' }}>This will permanently remove the email template.</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmDelete(null)} style={{ padding: '9px 20px', borderRadius: 8, border: '1.5px solid #d1d5db', background: 'transparent', color: isDark ? '#f1f5f9' : '#374151', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleDeleteConfirmed} style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: '#ef4444', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Send to Candidate sub-tab ─────────────────────────────────────────────────
function SendToCandidateTab({ templates: allTemplates, candidates, requirements, prefillRequirementId, prefillCandidateId }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  // Developer role is read-only for email composing — no preview, no send.
  const { isDeveloper } = useAuth();
  // Only show templates tagged for 'candidate' or 'both'
  const templates = allTemplates.filter(t => !t.targetAudience || t.targetAudience === 'candidate' || t.targetAudience === 'both');
  const [mode, setMode]             = useState('single'); // 'single' | 'bulk'
  const [form, setForm] = useState({ templateId: '', candidateId: '', requirementId: '', subject: '', body: '', extraVars: {} });
  const [preview, setPreview]       = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending]       = useState(false);
  const [reqCandidates, setReqCandidates] = useState([]);
  const [loadingCands, setLoadingCands]   = useState(false);
  const [toEmails, setToEmails] = useState([]);
  const [ccEmails, setCcEmails] = useState([]);
  const singleBodyRef = React.useRef(null);

  // Auto-select requirement + candidate when arriving via "Email Candidate" button
  useEffect(() => {
    if (prefillRequirementId && requirements.length > 0) {
      const match = requirements.find(r => r.id === prefillRequirementId);
      if (match) setForm(p => ({ ...p, requirementId: prefillRequirementId, candidateId: prefillCandidateId || '' }));
    }
    // Pre-populate To field from prefilled candidate
    if (prefillCandidateId) {
      const cand = candidates.find(c => c.id === prefillCandidateId);
      if (cand?.email) setToEmails([cand.email]);
    }
  }, [prefillRequirementId, prefillCandidateId, requirements, candidates]);

  // When requirement changes, fetch candidates linked via submissions
  useEffect(() => {
    const reqId = form.requirementId;
    if (!reqId) { setReqCandidates([]); return; }
    setLoadingCands(true);
    submissionAPI.getAll({ requirementId: reqId, limit: 500 })
      .then(({ data }) => {
        const seen = new Set();
        const list = [];
        for (const sub of (data.submissions || [])) {
          // Skip if candidate is missing or soft-deleted
          if (!sub.candidate || !sub.candidateId || sub.candidate.isDeleted) continue;
          if (!seen.has(sub.candidateId)) {
            seen.add(sub.candidateId);
            list.push({
              id:                      sub.candidateId,
              fullName:                sub.candidate.fullName || '—',
              email:                   sub.candidate.email   || '',
              // Submission-level billing override — takes priority over requirement billing
              overrideBillingCost:     sub.overrideBillingCost     ?? null,
              overrideBillingCurrency: sub.overrideBillingCurrency ?? null,
              overrideBillingType:     sub.overrideBillingType     ?? null,
            });
          }
        }
        setReqCandidates(list);
        // Auto-select if only one candidate is linked
        if (list.length === 1 && !form.candidateId) {
          setForm(p => ({ ...p, candidateId: list[0].id }));
        }
      })
      .catch(() => setReqCandidates([]))
      .finally(() => setLoadingCands(false));
  }, [form.requirementId]);

  const selectedTemplate    = templates.find(t => t.id === form.templateId);
  const selectedRequirement = requirements.find(r => r.id === form.requirementId);

  // When a requirement is selected, only show candidates linked via submissions.
  // When no requirement is selected, show all candidates so user can still email anyone.
  const filteredCandidates = form.requirementId ? reqCandidates : candidates;

  // Variables the user must fill in manually (not auto-filled by the system)
  const requiredVars     = getRequiredVars(selectedTemplate);
  const allRequiredFilled = requiredVars.every(v => (form.extraVars[v] || '').trim() !== '');

  // Resolved BCC preview (informational). Candidate emails BCC the requirement
  // contact person AND the template BCC. Contact takes precedence on dupes, so
  // template chips exclude any address already shown under the contact source.
  const bccContact  = parseBccList(reqContactEmails(selectedRequirement));
  const bccTemplate = parseBccList(selectedTemplate?.bcc)
    .filter(e => !bccContact.some(c => c.toLowerCase() === e.toLowerCase()));

  const unresolvedVars      = preview ? [...(form.body || '').matchAll(/\{\{(\w+)\}\}/g)].map(m => m[1]) : [];

  const reset = () => {
    setForm({ templateId: '', candidateId: '', requirementId: '', subject: '', body: '', extraVars: {} });
    setPreview(null); setToEmails([]); setCcEmails([]);
  };

  const handlePreview = async () => {
    if (!form.templateId || !form.candidateId) return toast.error('Select a template and a candidate first.');
    setPreviewing(true);
    try {
      const extraVars = {
        ...form.extraVars,
        ...buildReqVars(selectedRequirement),
      };
      const { data } = await emailAPI.preview({ templateId: form.templateId, candidateId: form.candidateId, requirementId: form.requirementId || undefined, extraVars });
      setPreview(data.preview);
      setForm(p => ({ ...p, subject: data.preview.subject, body: data.preview.body }));
      // Pre-fill To with the candidate's email from the preview
      if (data.preview.to && toEmails.length === 0) setToEmails([data.preview.to]);
      // Set contentEditable body after React mounts the div (setTimeout defers until next tick)
      setTimeout(() => { if (singleBodyRef.current) singleBodyRef.current.innerHTML = bodyToHtml(data.preview.body); }, 0);
    } catch (err) { toast.error(err?.response?.data?.message || 'Preview failed.'); }
    finally { setPreviewing(false); }
  };

  const handleSend = async () => {
    if (toEmails.length === 0) return toast.error('Add at least one recipient in To.');
    if (!form.subject || !form.body) return toast.error('Subject and body are required.');
    if (!form.candidateId) return toast.error('Please select a candidate.');
    setSending(true);
    try {
      await emailAPI.send({
        templateId:   form.templateId || undefined,
        candidateId:  form.candidateId,
        requirementId: form.requirementId || undefined,
        to:           toEmails,          // honour the edited "To" (not the candidate's stored email)
        subject:      form.subject,
        body:         form.body,
        cc:           ccEmails.length > 0 ? ccEmails : undefined,
      });
      toast.success('Email sent to candidate!');
      reset();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to send email.');
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-4">
      {/* Single / Bulk mode toggle */}
      <div style={{ background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 8, padding: 4, display: 'flex', flexWrap: 'wrap' }}>
        <Button size="xs" variant={mode === 'single' ? 'primary' : 'ghost'} onClick={() => setMode('single')}>✉ Single Candidate</Button>
        <Button size="xs" variant={mode === 'bulk' ? 'primary' : 'ghost'} onClick={() => setMode('bulk')}>📨 Bulk Send</Button>
      </div>

      {/* Bulk mode — delegate to BulkToCandidatesTab */}
      {mode === 'bulk' && (
        <BulkToCandidatesTab templates={allTemplates} candidates={candidates} requirements={requirements} />
      )}

      {/* Single mode */}
      {mode === 'single' && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="space-y-4">

        {/* Prefill banner — shown when arriving from a requirement card */}
        {prefillRequirementId && selectedRequirement && (
          <div className="flex items-center gap-3 bg-indigo-500/10 border border-indigo-500/30 rounded-xl px-4 py-3">
            <span className="text-2xl">📋</span>
            <div className="flex-1 min-w-0">
              <p className="text-indigo-300 text-sm font-medium">{selectedRequirement.jobTitle}</p>
              <p className="text-indigo-400/70 text-xs">
                {selectedRequirement.client?.clientName && `${selectedRequirement.client.clientName} · `}
                Pre-filled from Requirements
              </p>
            </div>
            <Button variant="ghost" size="xs" iconOnly onClick={() => setForm(p => ({ ...p, requirementId: '', candidateId: '' }))} tooltip="Clear pre-fill" style={{ flexShrink: 0 }}>×</Button>
          </div>
        )}

        {/* Template */}
        <TemplatePicker
          label="Template (optional)"
          value={form.templateId}
          onChange={v => { setForm(p => ({ ...p, templateId: v, subject: '', body: '', extraVars: {} })); setPreview(null); }}
          templates={templates}
        />

        {/* Requirement — shown first so candidate list filters accordingly */}
        <SearchableSelect
          label="Requirement (optional — filters candidates & auto-fills job details)"
          value={form.requirementId}
          onChange={v => {
            setForm(p => ({ ...p, requirementId: v, candidateId: '', subject: '', body: '' }));
            setPreview(null);
          }}
          placeholder="— Select a requirement —"
          options={requirements.map(r => ({
            value: r.id,
            label: r.jobTitle,
            sub:   r.client?.clientName || '',
          }))}
        />

        {/* Requirement details card */}
        {selectedRequirement && (
          <InfoCard icon="📋" title="Requirement details (auto-injected into template)" rows={[
            ['Job Title',   selectedRequirement.jobTitle],
            ['Location',    selectedRequirement.location],
            ['Work Mode',   selectedRequirement.workMode],
            ['Experience',  selectedRequirement.minExperience && `${selectedRequirement.minExperience}–${selectedRequirement.maxExperience} yrs`],
            ['Budget',      selectedRequirement.budgetMin && `${selectedRequirement.budgetMin}–${selectedRequirement.budgetMax}`],
            ['Openings',    selectedRequirement.openings],
            ['Skills',      Array.isArray(selectedRequirement.requiredSkills) ? selectedRequirement.requiredSkills.join(', ') : ''],
          ]} />
        )}

        {/* Candidate */}
        <div>
          <SearchableSelect
            label={form.requirementId
              ? `Candidate * ${loadingCands ? '(loading…)' : `(${filteredCandidates.length} linked)`}`
              : 'Candidate *'}
            value={form.candidateId}
            onChange={v => {
              setForm(p => ({ ...p, candidateId: v }));
              setPreview(null);
              // Auto-populate To field with the selected candidate's email
              const picked = filteredCandidates.find(c => c.id === v);
              if (picked?.email) setToEmails([picked.email]);
            }}
            placeholder={loadingCands ? 'Loading candidates…' : form.requirementId ? '— Select a linked candidate —' : '— Search all candidates —'}
            options={filteredCandidates.map(c => ({
              value: c.id,
              label: c.fullName,
              sub:   c.email,
            }))}
          />
          {form.requirementId && !loadingCands && filteredCandidates.length === 0 && (
            <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>
              No candidates are submitted for this requirement yet.
            </p>
          )}
          {!form.requirementId && (
            <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>
              💡 Select a requirement above to see only its linked candidates.
            </p>
          )}
        </div>

        {/* Auto-detected required variables — user must fill these before sending */}
        {requiredVars.length > 0 && (
          <div style={{ background: 'var(--bg-raised)', border: '1px solid rgba(245,158,11,0.35)', borderRadius: 12, padding: 16 }} className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-amber-400 text-sm">⚠</span>
              <p className="text-xs text-amber-400 font-medium">Required fields — fill all before sending</p>
            </div>
            <p style={{ color: 'var(--text-muted)' }} className="text-xs">These variables were found in the template and are not auto-filled by the system.</p>
            <div className="space-y-2">
              {requiredVars.map(v => (
                <div key={v}>
                  <label style={{ color: 'var(--text-secondary)', fontSize: 12, display: 'block', marginBottom: 4, fontWeight: 500 }}>{varLabel(v)}</label>
                  <input
                    value={form.extraVars[v] || ''}
                    onChange={e => setForm(p => ({ ...p, extraVars: { ...p.extraVars, [v]: e.target.value } }))}
                    placeholder={`Enter ${v.replace(/_/g, ' ')}`}
                    style={{ background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid ' + ((form.extraVars[v] || '').trim() ? 'rgba(34,197,94,0.5)' : 'rgba(245,158,11,0.4)') }}
                    className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none transition-colors"
                  />
                </div>
              ))}
            </div>
            {/* Progress indicator */}
            <div className="flex items-center gap-2 pt-1">
              <div style={{ background: 'var(--bg-raised)' }} className="flex-1 rounded-full h-1">
                <div
                  className="bg-amber-400 h-1 rounded-full transition-all"
                  style={{ width: `${requiredVars.length ? (requiredVars.filter(v => (form.extraVars[v] || '').trim()).length / requiredVars.length) * 100 : 0}%` }}
                />
              </div>
              <span style={{ color: 'var(--text-muted)' }} className="text-xs">
                {requiredVars.filter(v => (form.extraVars[v] || '').trim()).length}/{requiredVars.length} filled
              </span>
            </div>
          </div>
        )}

        <Button variant="outline" onClick={handlePreview} disabled={isDeveloper || previewing || !form.templateId || !form.candidateId} loading={previewing} loadingText="Loading preview..." title={isDeveloper ? 'Disabled for Developer role' : ''} style={{ width: '100%' }}>👁 Preview email</Button>

        {form.candidateId && (
          <>
            <TextField label="Subject" value={form.subject} onChange={v => setForm(p => ({ ...p, subject: v }))}
              placeholder={form.templateId ? 'Click Preview to auto-fill, or type manually' : 'Enter email subject'} />
            <div>
              <label style={{ color: 'var(--text-muted)', fontSize: 12, display: 'block', marginBottom: 4 }}>Body</label>
              <textarea value={form.body} onChange={e => setForm(p => ({ ...p, body: e.target.value }))} rows={10}
                placeholder={form.templateId ? 'Click Preview to auto-fill, or type manually' : 'Enter email body'}
                style={{ background: 'var(--bg-input)', border: '1px solid var(--border-input)', color: 'var(--text-primary)' }}
                className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none font-mono resize-none" />
            </div>
          </>
        )}

        {unresolvedVars.length > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
            <p className="text-amber-300 text-xs font-medium mb-1">⚠ Unfilled variables</p>
            <p className="text-amber-400/70 text-xs">Still need values: {unresolvedVars.map(v => `{{${v}}}`).join(', ')}</p>
          </div>
        )}

        {requiredVars.length > 0 && !allRequiredFilled && (
          <p className="text-xs text-amber-400 text-center">Fill in all required fields above to enable sending</p>
        )}
        <Button variant="primary" onClick={handleSend} disabled={isDeveloper || sending || toEmails.length === 0 || !form.subject || !form.body || !allRequiredFilled} loading={sending} loadingText="Sending..." title={isDeveloper ? 'Disabled for Developer role' : ''} style={{ width: '100%' }}>✉ Send to Candidate</Button>
      </div>

      {/* Preview panel */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 style={{ color: 'var(--text-primary)' }} className="font-medium">Preview</h3>
          {preview && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>✏ Click anywhere to edit</span>}
        </div>
        {!preview ? (
          <div style={{ background: 'var(--bg-raised)', border: '1px dashed var(--border-subtle)', color: 'var(--text-muted)' }} className="rounded-xl flex items-center justify-center h-64 text-sm text-center px-6">
            Select a template and candidate, then click "Preview email"
          </div>
        ) : (
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)' }} className="rounded-xl overflow-hidden">
            <div className="px-4 pt-3 pb-1 space-y-0.5">
              <EmailTagInput label="To" emails={toEmails} onChange={setToEmails} isDark={isDark} />
              <EmailTagInput label="Cc" emails={ccEmails} onChange={setCcEmails} isDark={isDark} />
              {(bccContact.length > 0 || bccTemplate.length > 0) && (
                <div style={{ borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 0' }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 500, width: 28, flexShrink: 0, paddingTop: 3 }}>Bcc</span>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {bccContact.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                        <span style={{ fontSize: 10, fontWeight: 600, color: isDark ? '#67e8f9' : '#0e7490', background: isDark ? 'rgba(6,182,212,0.15)' : '#ecfeff', border: '1px solid ' + (isDark ? 'rgba(6,182,212,0.3)' : '#a5f3fc'), padding: '1px 6px', borderRadius: 4 }}>Requirement contact</span>
                        {bccContact.map(e => (
                          <span key={e} style={{ fontSize: 11, color: 'var(--text-secondary)', background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', padding: '1px 7px', borderRadius: 10 }}>{e}</span>
                        ))}
                      </div>
                    )}
                    {bccTemplate.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                        <span style={{ fontSize: 10, fontWeight: 600, color: isDark ? '#a5b4fc' : '#4338ca', background: isDark ? 'rgba(99,102,241,0.15)' : '#eef2ff', border: '1px solid ' + (isDark ? 'rgba(99,102,241,0.3)' : '#c7d2fe'), padding: '1px 6px', borderRadius: 4 }}>Template</span>
                        {bccTemplate.map(e => (
                          <span key={e} style={{ fontSize: 11, color: 'var(--text-secondary)', background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', padding: '1px 7px', borderRadius: 10 }}>{e}</span>
                        ))}
                      </div>
                    )}
                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>🔒 Hidden from the candidate — sent as BCC. Shown here for your reference only.</span>
                  </div>
                </div>
              )}
              <div style={{ borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
                <span style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 500, width: 28, flexShrink: 0 }}>Sub</span>
                <input
                  value={form.subject}
                  onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                  style={{ background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: 12, flex: 1, padding: '2px 0' }}
                  placeholder="Enter subject…"
                />
              </div>
            </div>
            <div
              ref={singleBodyRef}
              contentEditable
              suppressContentEditableWarning
              onInput={e => { const v = e.currentTarget.innerHTML || e.currentTarget.innerText || ''; setForm(p => ({ ...p, body: v })); }}
              className="px-4 py-4 max-h-[600px] overflow-y-auto break-words"
              style={{
                outline: 'none', cursor: 'text', minHeight: 120, color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.6,
                borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}`,
                wordBreak: 'break-word', overflowWrap: 'anywhere',
              }}
            />
          </div>
        )}
      </div>
    </div>}
    </div>
  );
}

// ── Bulk Send to Candidates sub-tab ───────────────────────────────────────────
// Same template → multiple candidates of a requirement, each personalised.
function BulkToCandidatesTab({ templates: allTemplates, candidates, requirements }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { isDeveloper } = useAuth();
  const templates = allTemplates.filter(t => !t.targetAudience || t.targetAudience === 'candidate' || t.targetAudience === 'both');

  const [requirementId,  setRequirementId]  = useState('');
  const [templateId,     setTemplateId]     = useState('');
  const [selectedIds,    setSelectedIds]    = useState([]);
  const [extraVars,      setExtraVars]      = useState({});
  const [preview,        setPreview]        = useState(null);
  const [previewing,     setPreviewing]     = useState(false);
  const [sending,        setSending]        = useState(false);
  const [results,        setResults]        = useState(null);
  const [reqCandidates,  setReqCandidates]  = useState([]);
  const [loadingCands,   setLoadingCands]   = useState(false);
  const [ccEmails,       setCcEmails]       = useState([]);
  const [previewSubject, setPreviewSubject] = useState('');
  // Body value is intentionally unread: the preview body is a contentEditable
  // div that owns its own DOM content, so only the setter is needed.
  const [, setPreviewBody] = useState('');
  const bulkBodyRef = React.useRef(null);

  const selectedRequirement = requirements.find(r => r.id === requirementId);
  const selectedTemplate    = templates.find(t => t.id === templateId);

  // Fetch candidates via submissions when a requirement is selected
  useEffect(() => {
    if (!requirementId) { setReqCandidates([]); return; }
    setLoadingCands(true);
    submissionAPI.getAll({ requirementId, limit: 500 })
      .then(({ data }) => {
        // Build flat list: candidate info + submission status, deduplicated by candidateId
        // Skip soft-deleted candidates (sub.candidate will be null)
        const seen = new Set();
        const list = [];
        for (const sub of (data.submissions || [])) {
          if (!sub.candidate || !sub.candidateId || sub.candidate.isDeleted) continue;
          if (!seen.has(sub.candidateId)) {
            seen.add(sub.candidateId);
            list.push({ id: sub.candidateId, fullName: sub.candidate.fullName || '—', email: sub.candidate.email || '', status: sub.status });
          }
        }
        setReqCandidates(list);
      })
      .catch(() => setReqCandidates([]))
      .finally(() => setLoadingCands(false));
  }, [requirementId]);

  const allChecked = reqCandidates.length > 0 && selectedIds.length === reqCandidates.length;

  // Variables the user must fill in manually
  const requiredVars      = getRequiredVars(selectedTemplate);
  const allRequiredFilled = requiredVars.every(v => (extraVars[v] || '').trim() !== '');

  const toggleAll = () =>
    setSelectedIds(allChecked ? [] : reqCandidates.map(c => c.id));

  const toggleOne = (id) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  // Preview uses the first selected candidate as a sample
  const handlePreview = async () => {
    if (!templateId || selectedIds.length === 0) return toast.error('Select a template and at least one candidate.');
    setPreviewing(true);
    try {
      const sampleId = selectedIds[0];
      const reqVars = selectedRequirement ? {
        job_title:       selectedRequirement.jobTitle,
        location:        selectedRequirement.location || '',
        work_mode:       selectedRequirement.workMode || '',
        min_experience:  selectedRequirement.minExperience || '',
        max_experience:  selectedRequirement.maxExperience || '',
        required_skills: Array.isArray(selectedRequirement.requiredSkills) ? selectedRequirement.requiredSkills.join(', ') : '',
        openings:        selectedRequirement.openings || '',
      } : {};
      const { data } = await emailAPI.preview({ templateId, candidateId: sampleId, extraVars: { ...extraVars, ...reqVars } });
      setPreview(data.preview);
      setPreviewSubject(data.preview?.subject || '');
      setPreviewBody(data.preview?.body || '');
      // Set contentEditable body after React mounts the div (setTimeout defers until next tick)
      setTimeout(() => { if (bulkBodyRef.current) bulkBodyRef.current.innerHTML = bodyToHtml(data.preview?.body); }, 0);
    } catch (err) { toast.error(err?.response?.data?.message || 'Preview failed.'); }
    finally { setPreviewing(false); }
  };

  const handleSend = async () => {
    if (!templateId)              return toast.error('Select a template.');
    if (selectedIds.length === 0) return toast.error('Select at least one candidate.');
    setSending(true); setResults(null);
    try {
      const { data } = await emailAPI.sendBulk({
        templateId,
        candidateIds:  selectedIds,
        requirementId: requirementId || undefined,
        extraVars,
        cc:            ccEmails.length > 0 ? ccEmails : undefined,
      });
      setResults(data);
      toast.success(data.message);
      // Keep selection so user can review results; clear template/preview
      setPreview(null); setTemplateId(''); setSelectedIds([]);
    } catch { toast.error('Bulk send failed.'); }
    finally { setSending(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* ── Left panel: controls ── */}
      <div className="space-y-4">

        {/* Info banner */}
        <div className="flex items-center gap-2 bg-indigo-500/10 border border-indigo-500/30 rounded-xl px-4 py-3">
          <span className="text-xl">📨</span>
          <p className="text-indigo-300 text-sm">Select a requirement, pick candidates, choose a template — each person gets a personalised email.</p>
        </div>

        {/* Requirement selector */}
        <SearchableSelect
          label="Requirement *"
          value={requirementId}
          onChange={v => { setRequirementId(v); setSelectedIds([]); setPreview(null); setResults(null); setReqCandidates([]); }}
          placeholder="— Select a requirement —"
          options={requirements.map(r => ({
            value: r.id,
            label: r.jobTitle,
            sub:   r.client?.clientName || '',
          }))}
        />

        {/* Candidate checklist */}
        {requirementId && (
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)' }} className="rounded-xl overflow-hidden">
            {/* Header row */}
            <div style={{ borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-raised)' }} className="flex items-center justify-between px-4 py-2.5">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={allChecked} onChange={toggleAll}
                  className="w-4 h-4 rounded accent-indigo-500" />
                <span style={{ color: 'var(--text-secondary)' }} className="text-xs font-medium">
                  {loadingCands
                    ? 'Loading candidates…'
                    : reqCandidates.length === 0
                      ? 'No submissions found for this requirement'
                      : `Select all (${reqCandidates.length} submitted)`}
                </span>
              </label>
              {selectedIds.length > 0 && (
                <span className="text-indigo-400 text-xs font-semibold">{selectedIds.length} selected</span>
              )}
            </div>

            {/* Candidate rows */}
            {reqCandidates.length > 0 && (
              <div className="max-h-56 overflow-y-auto">
                {reqCandidates.map(c => {
                  const statusStyle = {
                    Applied:     { background: isDark ? 'rgba(100,116,139,0.2)' : '#f1f5f9', color: isDark ? '#94a3b8' : '#475569', border: '1px solid ' + (isDark ? 'rgba(100,116,139,0.3)' : '#cbd5e1') },
                    Shortlisted: { background: isDark ? 'rgba(245,158,11,0.15)' : '#fffbeb', color: isDark ? '#fcd34d' : '#b45309', border: '1px solid ' + (isDark ? 'rgba(245,158,11,0.3)' : '#fde68a') },
                    Interview:   { background: isDark ? 'rgba(59,130,246,0.15)' : '#eff6ff', color: isDark ? '#93c5fd' : '#1d4ed8', border: '1px solid ' + (isDark ? 'rgba(59,130,246,0.3)' : '#bfdbfe') },
                    Hired:       { background: isDark ? 'rgba(16,185,129,0.15)' : '#ecfdf5', color: isDark ? '#6ee7b7' : '#047857', border: '1px solid ' + (isDark ? 'rgba(16,185,129,0.3)' : '#a7f3d0') },
                    Rejected:    { background: isDark ? 'rgba(239,68,68,0.15)' : '#fef2f2', color: isDark ? '#fca5a5' : '#b91c1c', border: '1px solid ' + (isDark ? 'rgba(239,68,68,0.3)' : '#fecaca') },
                  }[c.status] || { background: 'var(--bg-raised)', color: 'var(--text-muted)', border: '1px solid var(--border-subtle)' };
                  return (
                  <label key={c.id} style={{ borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={selectedIds.includes(c.id)} onChange={() => toggleOne(c.id)}
                      className="w-4 h-4 rounded accent-indigo-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p style={{ color: 'var(--text-primary)' }} className="text-xs font-medium truncate">{c.fullName}</p>
                      <p style={{ color: 'var(--text-muted)' }} className="text-xs truncate">{c.email}</p>
                    </div>
                    <span style={{ ...statusStyle, fontSize: 11, padding: '2px 6px', borderRadius: 9999, flexShrink: 0 }}>{c.status}</span>
                  </label>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Template selector */}
        <TemplatePicker
          label="Template *"
          required
          value={templateId}
          onChange={v => { setTemplateId(v); setPreview(null); setExtraVars({}); }}
          templates={templates}
        />


        {/* Auto-detected required variables */}
        {requiredVars.length > 0 && (
          <div style={{ background: 'var(--bg-raised)', border: '1px solid rgba(245,158,11,0.35)', borderRadius: 12, padding: 16 }} className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-amber-400 text-sm">⚠</span>
              <p className="text-xs text-amber-400 font-medium">Required fields — fill all before sending</p>
            </div>
            <p style={{ color: 'var(--text-muted)' }} className="text-xs">These variables are found in the template and not auto-filled. Each candidate email will use the same values.</p>
            <div className="space-y-2">
              {requiredVars.map(v => (
                <div key={v}>
                  <label style={{ color: 'var(--text-secondary)', fontSize: 12, display: 'block', marginBottom: 4, fontWeight: 500 }}>{varLabel(v)}</label>
                  <input
                    value={extraVars[v] || ''}
                    onChange={e => setExtraVars(p => ({ ...p, [v]: e.target.value }))}
                    placeholder={`Enter ${v.replace(/_/g, ' ')}`}
                    style={{ background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid ' + ((extraVars[v] || '').trim() ? 'rgba(34,197,94,0.5)' : 'rgba(245,158,11,0.4)') }}
                    className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none transition-colors"
                  />
                </div>
              ))}
            </div>
            {/* Progress indicator */}
            <div className="flex items-center gap-2 pt-1">
              <div style={{ background: 'var(--bg-raised)' }} className="flex-1 rounded-full h-1">
                <div
                  className="bg-amber-400 h-1 rounded-full transition-all"
                  style={{ width: `${requiredVars.length ? (requiredVars.filter(v => (extraVars[v] || '').trim()).length / requiredVars.length) * 100 : 0}%` }}
                />
              </div>
              <span style={{ color: 'var(--text-muted)' }} className="text-xs">
                {requiredVars.filter(v => (extraVars[v] || '').trim()).length}/{requiredVars.length} filled
              </span>
            </div>
          </div>
        )}

        {/* Preview button */}
        <Button variant="outline" onClick={handlePreview} disabled={previewing || !templateId || selectedIds.length === 0} loading={previewing} loadingText="Loading preview..." style={{ width: '100%' }}>{`👁 Preview (sample — ${selectedIds.length > 0 ? candidates.find(c=>c.id===selectedIds[0])?.fullName || 'first candidate' : 'select candidates first'})`}</Button>

        {/* Send button */}
        {requiredVars.length > 0 && !allRequiredFilled && templateId && (
          <p className="text-xs text-amber-400 text-center">Fill in all required fields above to enable sending</p>
        )}
        <Button variant="primary" onClick={handleSend} disabled={isDeveloper || sending || !templateId || selectedIds.length === 0 || !allRequiredFilled} loading={sending} loadingText={`Sending to ${selectedIds.length} candidate${selectedIds.length !== 1 ? 's' : ''}…`} title={isDeveloper ? 'Disabled for Developer role' : ''} style={{ width: '100%' }}>{`📨 Send to ${selectedIds.length} candidate${selectedIds.length !== 1 ? 's' : ''}`}</Button>
      </div>

      {/* ── Right panel: preview + results ── */}
      <div className="space-y-4">

        {/* Send results summary */}
        {results && (
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)' }} className="rounded-xl overflow-hidden">
            <div style={{ borderBottom: '1px solid var(--border-subtle)' }} className="flex items-center gap-3 px-4 py-3">
              <span className="text-lg">{results.failed === 0 ? '✅' : '⚠️'}</span>
              <p style={{ color: 'var(--text-primary)' }} className="text-sm font-medium">{results.message}</p>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {results.results.map(r => (
                <div key={r.candidateId} style={{ borderBottom: '1px solid var(--border-subtle)' }} className="flex items-center justify-between px-4 py-2.5">
                  <div>
                    <p style={{ color: 'var(--text-primary)' }} className="text-xs font-medium">{r.name}</p>
                    <p style={{ color: 'var(--text-muted)' }} className="text-xs">{r.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {r.status === 'sent'
                      ? <span className="text-xs bg-green-500/20 text-green-300 border border-green-500/30 px-2 py-0.5 rounded-full">✓ sent</span>
                      : <span className="text-xs bg-red-500/20 text-red-300 border border-red-500/30 px-2 py-0.5 rounded-full" title={r.error}>✗ failed</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Email preview */}
        <h3 style={{ color: 'var(--text-primary)' }} className="font-medium">Preview
          {preview && <span style={{ color: 'var(--text-muted)' }} className="text-xs font-normal ml-2">(sample — {preview.to})</span>}
        </h3>
        {!preview ? (
          <div style={{ background: 'var(--bg-raised)', border: '1px dashed var(--border-subtle)', color: 'var(--text-muted)' }} className="rounded-xl flex items-center justify-center h-64 text-sm text-center px-6">
            Select candidates and a template, then click Preview to see a sample email
          </div>
        ) : (
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)' }} className="rounded-xl overflow-hidden">
            <div className="px-4 pt-3 pb-1 space-y-0.5">
              {/* To — read-only sample; each candidate gets their own */}
              <div style={{ borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
                <span style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 500, width: 28, flexShrink: 0 }}>To</span>
                <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{preview.to}</span>
                <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 99, background: isDark ? 'rgba(245,158,11,0.15)' : '#fffbeb', color: isDark ? '#fcd34d' : '#b45309', border: '1px solid ' + (isDark ? 'rgba(245,158,11,0.3)' : '#fde68a'), marginLeft: 'auto', flexShrink: 0 }}>sample</span>
              </div>
              <EmailTagInput label="Cc" emails={ccEmails} onChange={setCcEmails} isDark={isDark} />
              <div style={{ borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
                <span style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 500, width: 28, flexShrink: 0 }}>Sub</span>
                <input
                  value={previewSubject}
                  onChange={e => setPreviewSubject(e.target.value)}
                  style={{ background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: 12, flex: 1, padding: '2px 0' }}
                  placeholder="Enter subject…"
                />
              </div>
            </div>
            <div className="px-4 pt-2 pb-1">
              <p className="text-xs text-amber-400/70">⚡ Each recipient gets their own personalised copy</p>
            </div>
            <div
              ref={bulkBodyRef}
              contentEditable
              suppressContentEditableWarning
              onInput={e => { const v = e.currentTarget.innerHTML || e.currentTarget.innerText || ''; setPreviewBody(v); }}
              className="px-4 py-4 max-h-[400px] overflow-y-auto break-words"
              style={{
                outline: 'none', cursor: 'text', minHeight: 120, color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.6,
                borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}`,
                wordBreak: 'break-word', overflowWrap: 'anywhere',
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Send to Client sub-tab ────────────────────────────────────────────────────
function SendToClientTab({ templates: allTemplates, candidates, requirements, prefillCandidateId, prefillRequirementId, prefillSource }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { isDeveloper } = useAuth();
  // Show templates tagged for 'client' or 'both', but EXCLUDE batch templates
  // (those with {{candidates_table}}). A single send is about ONE candidate, so
  // the multi-candidate table template belongs only to the "Batch to Client" tab.
  const templates = allTemplates.filter(t =>
    (!t.targetAudience || t.targetAudience === 'client' || t.targetAudience === 'both') &&
    !hasBatchTableVar(t)
  );
  const [form, setForm] = useState({ templateId: '', candidateId: '', requirementId: '', clientContact: null, subject: '', body: '', extraVars: {} });
  const [preview, setPreview]         = useState(null);
  const [previewing, setPreviewing]   = useState(false);
  const [sending, setSending]         = useState(false);
  const [toEmails, setToEmails]       = useState([]);
  const [ccEmails, setCcEmails]       = useState([]);
  const [attachResume, setAttachResume] = useState(true);
  const [reqCandidates, setReqCandidates] = useState([]); // candidates linked to the chosen requirement
  const [loadingCands, setLoadingCands]   = useState(false);
  const clientBodyRef = React.useRef(null);

  // Auto-select candidate + requirement when arriving via "Email to Client" shortcut
  // Works from both Candidates page (prefillCandidateId only) and Submissions page (both IDs)
  useEffect(() => {
    if (prefillCandidateId && candidates.length > 0) {
      const match = candidates.find(c => c.id === prefillCandidateId);
      if (match) {
        setForm(p => ({
          ...p,
          candidateId:   prefillCandidateId,
          requirementId: prefillRequirementId || match.requirement?.id || match.requirementId || '',
        }));
      }
    }
  }, [prefillCandidateId, prefillRequirementId, candidates]);

  // Auto-select the first available client contact once the requirement is loaded (from submissions)
  useEffect(() => {
    if (!prefillRequirementId) return;
    const req = requirements.find(r => r.id === prefillRequirementId);
    if (!req) return;
    const contacts = Array.isArray(req.contacts) && req.contacts.length > 0
      ? req.contacts
      : req.client?.contactEmail
        ? [{ name: req.client.clientName || 'Client', email: req.client.contactEmail }]
        : [];
    if (contacts.length > 0) {
      setForm(p => ({ ...p, clientContact: p.clientContact || contacts[0] }));
    }
  }, [prefillRequirementId, requirements]);

  // Resolve the selected candidate from the requirement-linked list first (so
  // submission-level billing overrides apply), falling back to the global list
  // for a pre-filled candidate while their linked list is still loading.
  const selectedCandidate   = reqCandidates.find(c => c.id === form.candidateId)
                           || candidates.find(c => c.id === form.candidateId);
  const selectedRequirement = requirements.find(r => r.id === form.requirementId);

  // Auto-add the requirement's defaultCcEmail to the CC tag input when the
  // requirement is picked. When the requirement is SWITCHED, the previous
  // requirement's auto-CCs are removed first so emails don't pile up across
  // selections. User-typed emails are preserved.
  // autoCcRef remembers which emails were inserted by this effect so we know
  // what to subtract before re-adding the new requirement's defaults.
  const autoCcRef = useRef([]);
  useEffect(() => {
    const raw = selectedRequirement?.defaultCcEmail || '';
    const newDefaults = raw.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
    // Snapshot the PREVIOUS defaults BEFORE updating the ref. The setCcEmails
    // callback is queued and runs later; if we read autoCcRef.current from
    // inside the callback it would see the new value (since we update the ref
    // synchronously below) and the filter would no-op, causing pile-up.
    const oldDefaults = autoCcRef.current;
    autoCcRef.current = newDefaults;

    setCcEmails(prev => {
      // Drop any emails we previously auto-added (they belong to the old req)
      const userTyped = prev.filter(e => !oldDefaults.includes(e.toLowerCase()));
      // Merge in the new requirement's defaults, deduped against user-typed
      const seen = new Set(userTyped.map(e => e.toLowerCase()));
      const merged = [...userTyped];
      for (const d of newDefaults) {
        if (!seen.has(d)) {
          merged.push(d);
          seen.add(d);
        }
      }
      return merged;
    });
  }, [selectedRequirement?.id, selectedRequirement?.defaultCcEmail]);

  // Keep the To field in sync with the selected client contact. When the
  // contact changes (via requirement switch or user picking a different
  // person), drop the previously auto-added contact email and add the new
  // one. User-typed emails in To are preserved by tracking what we
  // auto-added in autoToRef. Same closure/timing fix as CC above —
  // snapshot the old value BEFORE updating the ref.
  const autoToRef = useRef(null);
  useEffect(() => {
    const newEmail = form.clientContact?.email || null;
    const oldEmail = autoToRef.current;
    autoToRef.current = newEmail;

    setToEmails(prev => {
      const userTyped = oldEmail
        ? prev.filter(e => e.toLowerCase() !== oldEmail.toLowerCase())
        : [...prev];
      if (newEmail && !userTyped.some(e => e.toLowerCase() === newEmail.toLowerCase())) {
        return [newEmail, ...userTyped];
      }
      return userTyped;
    });
  }, [form.clientContact?.email]);

  // Fetch only the candidates linked to the selected requirement via submissions
  // (same source as the "Send to Candidate" and "Batch to Client" tabs). Each
  // entry keeps full candidate fields plus the submission's billing overrides.
  useEffect(() => {
    if (!form.requirementId) { setReqCandidates([]); return; }
    setLoadingCands(true);
    submissionAPI.getAll({ requirementId: form.requirementId, limit: 500 })
      .then(({ data }) => {
        const seen = new Set();
        const list = [];
        for (const sub of (data.submissions || [])) {
          if (!sub.candidate || !sub.candidateId || sub.candidate.isDeleted) continue;
          if (seen.has(sub.candidateId)) continue;
          seen.add(sub.candidateId);
          list.push({
            ...sub.candidate,
            id:                      sub.candidateId,
            fullName:                sub.candidate.fullName || '—',
            email:                   sub.candidate.email    || '',
            overrideBillingCost:     sub.overrideBillingCost     ?? null,
            overrideBillingCurrency: sub.overrideBillingCurrency ?? null,
            overrideBillingType:     sub.overrideBillingType     ?? null,
          });
        }
        setReqCandidates(list);
      })
      .catch(() => setReqCandidates([]))
      .finally(() => setLoadingCands(false));
  }, [form.requirementId]);

  // Only candidates linked to the chosen requirement are selectable. (Before a
  // requirement is picked the list is empty — requirement is required here.) A
  // pre-filled/selected candidate is kept visible even without a submission yet,
  // so arriving via "Email to Client" still shows the right person.
  const filteredCandidates = (() => {
    const base = form.requirementId ? reqCandidates : [];
    if (form.candidateId && !base.some(c => c.id === form.candidateId)) {
      const sel = candidates.find(c => c.id === form.candidateId);
      if (sel) return [sel, ...base];
    }
    return base;
  })();

  // Build client contacts from requirement.contacts array, fallback to client.contactEmail
  const clientContacts = (() => {
    if (!selectedRequirement) return [];
    const contacts = selectedRequirement.contacts;
    if (Array.isArray(contacts) && contacts.length > 0) return contacts;
    if (selectedRequirement.client?.contactEmail) {
      return [{ name: selectedRequirement.client.clientName || 'Client', email: selectedRequirement.client.contactEmail }];
    }
    return [];
  })();

  const unresolvedVars = preview ? [...(form.body || '').matchAll(/\{\{(\w+)\}\}/g)].map(m => m[1]) : [];

  const selectedTemplate    = templates.find(t => t.id === form.templateId);
  const requiredVars        = getRequiredVars(selectedTemplate);
  const allRequiredFilled   = requiredVars.every(v => (form.extraVars[v] || '').trim() !== '');

  const reset = () => {
    setForm({ templateId: '', candidateId: '', requirementId: '', clientContact: null, subject: '', body: '', extraVars: {} });
    setPreview(null); setToEmails([]); setCcEmails([]); setAttachResume(true);
  };

  // Format billing into human-readable string
  const fmt = (cost, currency, type) => {
    if (!cost) return '';
    const sym    = { USD: '$', EUR: '€', GBP: '£', INR: '₹', AED: 'AED ', SGD: 'S$', AUD: 'A$', CAD: 'C$', JPY: '¥', MYR: 'RM ', QAR: 'QAR ', SAR: 'SAR ' }[currency] || `${currency || ''} `;
    const suffix = type === 'Hourly' ? ' per hour' : type === 'Monthly' ? ' per month' : type === 'Percentage' ? '%' : '';
    const amount = type === 'Percentage' ? Number(cost) : `${sym}${Number(cost).toLocaleString()}`;
    return `${amount}${suffix} (${type || 'Fixed'})`;
  };

  // Resolve billing for selected candidate — override wins over requirement default
  const effectiveBillingCost     = selectedCandidate?.overrideBillingCost     ?? selectedRequirement?.billingCost;
  const effectiveBillingCurrency = selectedCandidate?.overrideBillingCurrency ?? selectedRequirement?.billingCurrency ?? 'USD';
  const effectiveBillingType     = selectedCandidate?.overrideBillingType     ?? selectedRequirement?.billingType;
  const resolvedBillingCost      = fmt(effectiveBillingCost, effectiveBillingCurrency, effectiveBillingType);

  const buildVars = () => ({
    candidate_name:        selectedCandidate?.fullName || '',
    candidate_email:       selectedCandidate?.email || '',
    candidate_phone:       selectedCandidate?.phone || '',
    candidate_experience:  selectedCandidate?.experience ? `${selectedCandidate.experience} years` : '',
    candidate_skills:      Array.isArray(selectedCandidate?.skills) ? selectedCandidate.skills.join(', ') : '',
    candidate_location:    selectedCandidate?.location || '',
    notice_period:         noticeText(selectedCandidate),
    offer_in_hand:         selectedCandidate?.offerInHand ? 'Yes' : 'No',
    // Raw (unformatted) legacy value here by design — the single-send preview
    // shows exactly what was stored; only the batch views normalise it.
    relevant_experience:   selectedCandidate?.relevantExperience || htmlToText(selectedCandidate?.experienceSummary),
    client_name:           form.clientContact?.name || '',
    client_email:          form.clientContact?.email || '',
    job_title:             selectedRequirement?.jobTitle || '',
    job_description:       selectedRequirement?.jobDescription || '',
    location:              selectedRequirement?.location || '',
    job_location:          selectedRequirement?.location || '',
    work_mode:             selectedRequirement?.workMode || '',
    // All requirement fields — every variable a template might use from the requirement
    ...buildReqVars(selectedRequirement),
    // Billing overrides: candidate-level billing beats requirement default
    billing_cost:          resolvedBillingCost,
    billing_type:          effectiveBillingType     || '',
    billing_currency:      effectiveBillingCurrency || '',
    ...form.extraVars,
  });

  const handlePreview = async () => {
    if (!form.requirementId || !form.candidateId) return toast.error('Select a candidate and requirement first.');
    if (!form.clientContact) return toast.error('Select a client contact to send to.');
    setPreviewing(true);
    try {
      if (form.templateId) {
        const { data } = await emailAPI.preview({ templateId: form.templateId, candidateId: form.candidateId, requirementId: form.requirementId || undefined, extraVars: buildVars() });
        setPreview({ ...data.preview, to: form.clientContact.email });
        setForm(p => ({ ...p, subject: data.preview.subject, body: data.preview.body }));
        // Set contentEditable body after React mounts the div (setTimeout defers until next tick)
        setTimeout(() => { if (clientBodyRef.current) clientBodyRef.current.innerHTML = bodyToHtml(data.preview.body); }, 0);
      } else {
        setPreview({ to: form.clientContact.email, subject: form.subject, body: form.body });
        setTimeout(() => { if (clientBodyRef.current) clientBodyRef.current.innerHTML = bodyToHtml(form.body); }, 0);
      }
      // Pre-fill To with the client contact email
      if (form.clientContact?.email && toEmails.length === 0) setToEmails([form.clientContact.email]);
    } catch (err) { toast.error(err?.response?.data?.message || 'Preview failed.'); }
    finally { setPreviewing(false); }
  };

  const handleSend = async () => {
    if (toEmails.length === 0) return toast.error('Add at least one recipient in To.');
    if (!form.subject || !form.body) return toast.error('Subject and body are required.');
    if (attachResume && form.candidateId && selectedCandidate && !selectedCandidate.resumeName) {
      return toast.error(`${selectedCandidate.fullName} has no resume on file — upload it first (Candidates → Edit).`);
    }
    setSending(true);
    try {
      await emailAPI.sendToClient({
        templateId:    form.templateId || undefined,
        candidateId:   form.candidateId || undefined,
        requirementId: form.requirementId || undefined,
        toEmail:       toEmails[0],
        toName:        form.clientContact?.name || '',
        subject:       form.subject,
        body:          form.body,
        to:            toEmails,
        cc:            ccEmails.length > 0 ? ccEmails : undefined,
        attachResume:  attachResume && !!form.candidateId,
      });
      toast.success(`Email sent to ${toEmails.join(', ')}!`);
      reset();
    } catch (err) { toast.error(err?.response?.data?.message || 'Failed to send email.'); }
    finally { setSending(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="space-y-4">

        {/* Prefill banner — shown when arriving from a candidate card or submission */}
        {prefillCandidateId && selectedCandidate && (
          <div className="flex items-center gap-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-4 py-3">
            <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0" style={{ color: '#fff' }}>
              {selectedCandidate.fullName?.[0]}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-emerald-300 text-sm font-medium">{selectedCandidate.fullName}</p>
              <p className="text-emerald-400/70 text-xs">
                {selectedCandidate.email} · Pre-filled from {prefillSource === 'submission' ? 'Submissions' : 'Candidates'}
              </p>
            </div>
            <Button variant="ghost" size="xs" iconOnly onClick={() => setForm(p => ({ ...p, candidateId: '', requirementId: '', clientContact: null }))} tooltip="Clear pre-fill" style={{ flexShrink: 0 }}>×</Button>
          </div>
        )}

        {/* Template */}
        <TemplatePicker
          label="Template (optional)"
          value={form.templateId}
          onChange={v => { setForm(p => ({ ...p, templateId: v, subject: '', body: '', extraVars: {} })); setPreview(null); }}
          templates={templates}
        />

        {/* Requirement — shown first so candidate list filters & client contacts load */}
        <SearchableSelect
          label="Requirement * (filters candidates & loads client contacts)"
          value={form.requirementId}
          onChange={v => { setForm(p => ({ ...p, requirementId: v, candidateId: '', clientContact: null, subject: '', body: '' })); setPreview(null); }}
          placeholder="— Select a requirement —"
          options={requirements.map(r => ({
            value: r.id,
            label: r.jobTitle,
            sub:   r.client?.clientName || '',
          }))}
        />

        {/* Candidate — filtered by selected requirement */}
        <div>
          <SearchableSelect
            label={form.requirementId
              ? `Candidate * ${loadingCands ? '(loading…)' : `(${filteredCandidates.length} linked)`}`
              : 'Candidate *'}
            value={form.candidateId}
            onChange={v => { setForm(p => ({ ...p, candidateId: v })); setPreview(null); }}
            placeholder={
              loadingCands
                ? 'Loading candidates…'
                : !form.requirementId
                  ? '— Select a requirement first —'
                  : filteredCandidates.length === 0
                    ? '— No candidates for this requirement —'
                    : `— Select a candidate (${filteredCandidates.length} found) —`
            }
            options={filteredCandidates.map(c => ({
              value: c.id,
              label: c.fullName,
              sub:   c.email,
            }))}
          />
          {form.requirementId && !loadingCands && filteredCandidates.length === 0 && (
            <p className="text-amber-400 text-xs mt-1">⚠ No candidates are linked to this requirement yet.</p>
          )}
          {form.requirementId && filteredCandidates.length > 0 && (
            <p style={{ color: 'var(--text-muted)' }} className="text-xs mt-1">Showing candidates mapped to the selected requirement.</p>
          )}
        </div>

        {/* Candidate details card */}
        {selectedCandidate && (
          <InfoCard icon="👤" title="Candidate details (auto-injected)" rows={[
            ['Name',           selectedCandidate.fullName],
            ['Email',          selectedCandidate.email],
            ['Experience',     selectedCandidate.experience && `${selectedCandidate.experience} years`],
            ['Skills',         Array.isArray(selectedCandidate.skills) ? selectedCandidate.skills.slice(0, 5).join(', ') : ''],
            ['Location',       selectedCandidate.location],
            ['Notice Period',  selectedCandidate.noticePeriod != null ? (selectedCandidate.noticePeriod === 0 ? 'Immediate' : `${selectedCandidate.noticePeriod} days`) : '—'],
            ['Offer In Hand',  selectedCandidate.offerInHand ? 'Yes' : 'No'],
            // Billing — show override if set, else show "—" (will inherit from requirement)
            ['Bill Rate',      selectedCandidate.overrideBillingCost
              ? `⚡ ${fmt(selectedCandidate.overrideBillingCost, selectedCandidate.overrideBillingCurrency || selectedRequirement?.billingCurrency, selectedCandidate.overrideBillingType || selectedRequirement?.billingType)} (override)`
              : resolvedBillingCost ? `${resolvedBillingCost} (from requirement)` : '—'],
          ]} />
        )}

        {/* Requirement details + client contacts */}
        {selectedRequirement && (
          <>
            <InfoCard icon="📋" title="Requirement details (auto-injected)" rows={[
              ['Job Title',     selectedRequirement.jobTitle],
              ['Location',      selectedRequirement.location],
              ['Work Mode',     selectedRequirement.workMode],
              ['Experience',    selectedRequirement.minExperience && `${selectedRequirement.minExperience}–${selectedRequirement.maxExperience} yrs`],
              ['Budget',        selectedRequirement.budgetMin && `${selectedRequirement.budgetMin}–${selectedRequirement.budgetMax}`],
              ['Openings',      selectedRequirement.openings],
              ['Skills',        Array.isArray(selectedRequirement.requiredSkills) ? selectedRequirement.requiredSkills.join(', ') : ''],
              ['Default Billing', fmt(selectedRequirement.billingCost, selectedRequirement.billingCurrency, selectedRequirement.billingType) || '—'],
            ]} />

            {/* Client contact picker */}
            {clientContacts.length === 0 ? (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                <p className="text-amber-300 text-xs font-medium">⚠ No client contacts found</p>
                <p className="text-amber-400/70 text-xs mt-1">Add contacts to this requirement or ensure the client has a contact email.</p>
              </div>
            ) : (
              <div>
                <label style={{ color: 'var(--text-muted)', fontSize: 12, display: 'block', marginBottom: 8 }}>Send to (Client Contact) *</label>
                <div className="space-y-2">
                  {clientContacts.map((contact, i) => (
                    <label key={i}
                      style={form.clientContact?.email === contact.email
                        ? { border: '1px solid #10b981', background: isDark ? 'rgba(16,185,129,0.1)' : '#ecfdf5', borderRadius: 12, padding: 12, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }
                        : { border: '1px solid var(--border-subtle)', background: 'var(--bg-raised)', borderRadius: 12, padding: 12, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
                      <input type="radio" name="clientContact" checked={form.clientContact?.email === contact.email}
                        onChange={() => setForm(p => ({ ...p, clientContact: contact }))} className="accent-emerald-500" />
                      <div className="flex-1 min-w-0">
                        <p style={{ color: 'var(--text-primary)' }} className="text-sm font-medium">{contact.name || 'Unnamed Contact'}</p>
                        <p style={{ color: 'var(--text-secondary)' }} className="text-xs">{contact.email}</p>
                      </div>
                      {form.clientContact?.email === contact.email && (
                        <span className="text-xs text-emerald-400">✓ Selected</span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Auto-detected required variables */}
        {requiredVars.length > 0 && (
          <div style={{ background: 'var(--bg-raised)', border: '1px solid rgba(245,158,11,0.35)', borderRadius: 12, padding: 16 }} className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-amber-400 text-sm">⚠</span>
              <p className="text-xs text-amber-400 font-medium">Required fields — fill all before sending</p>
            </div>
            <p style={{ color: 'var(--text-muted)' }} className="text-xs">These variables were found in the template and are not auto-filled by the system.</p>
            <div className="space-y-2">
              {requiredVars.map(v => (
                <div key={v}>
                  <label style={{ color: 'var(--text-secondary)', fontSize: 12, display: 'block', marginBottom: 4, fontWeight: 500 }}>{varLabel(v)}</label>
                  <input
                    value={form.extraVars[v] || ''}
                    onChange={e => setForm(p => ({ ...p, extraVars: { ...p.extraVars, [v]: e.target.value } }))}
                    placeholder={`Enter ${v.replace(/_/g, ' ')}`}
                    style={{ background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid ' + ((form.extraVars[v] || '').trim() ? 'rgba(34,197,94,0.5)' : 'rgba(245,158,11,0.4)') }}
                    className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none transition-colors"
                  />
                </div>
              ))}
            </div>
            {/* Progress bar */}
            <div className="flex items-center gap-2 pt-1">
              <div style={{ background: 'var(--bg-raised)' }} className="flex-1 rounded-full h-1">
                <div
                  className="bg-amber-400 h-1 rounded-full transition-all"
                  style={{ width: `${requiredVars.length ? (requiredVars.filter(v => (form.extraVars[v] || '').trim()).length / requiredVars.length) * 100 : 0}%` }}
                />
              </div>
              <span style={{ color: 'var(--text-muted)' }} className="text-xs">
                {requiredVars.filter(v => (form.extraVars[v] || '').trim()).length}/{requiredVars.length} filled
              </span>
            </div>
          </div>
        )}

        <Button variant="outline" onClick={handlePreview} disabled={isDeveloper || previewing || !form.candidateId || !form.requirementId || !form.clientContact} loading={previewing} loadingText="Loading preview..." title={isDeveloper ? 'Disabled for Developer role' : ''} style={{ width: '100%' }}>👁 Preview email</Button>

        {(preview || form.clientContact) && (
          <>
            <TextField label="Subject" value={form.subject} onChange={v => setForm(p => ({ ...p, subject: v }))} placeholder="Email subject" />
            <div>
              <label style={{ color: 'var(--text-muted)', fontSize: 12, display: 'block', marginBottom: 4 }}>Body</label>
              <textarea value={form.body} onChange={e => setForm(p => ({ ...p, body: e.target.value }))} rows={10}
                style={{ background: 'var(--bg-input)', border: '1px solid var(--border-input)', color: 'var(--text-primary)' }}
                className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none font-mono resize-none" />
            </div>
          </>
        )}

        {unresolvedVars.length > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
            <p className="text-amber-300 text-xs font-medium mb-1">⚠ Unfilled variables</p>
            <p className="text-amber-400/70 text-xs">Still need values: {unresolvedVars.map(v => `{{${v}}}`).join(', ')}</p>
          </div>
        )}

        {requiredVars.length > 0 && !allRequiredFilled && (
          <p className="text-xs text-amber-400 text-center">Fill in all required fields above to enable sending</p>
        )}
        {/* Attach Resume toggle — only shown when a candidate is selected */}
        {form.candidateId && selectedCandidate && (
          <label style={{
            display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
            background: attachResume ? (isDark ? 'rgba(16,185,129,0.08)' : '#f0fdf4') : 'var(--bg-raised)',
            border: `1px solid ${attachResume ? (isDark ? 'rgba(16,185,129,0.3)' : '#a7f3d0') : 'var(--border-subtle)'}`,
            borderRadius: 8, padding: '8px 12px', transition: 'all 0.15s',
          }}>
            <input
              type="checkbox"
              checked={attachResume}
              onChange={e => setAttachResume(e.target.checked)}
              className="accent-emerald-500 w-4 h-4 cursor-pointer"
            />
            <span style={{ fontSize: 13, color: attachResume ? (isDark ? '#6ee7b7' : '#047857') : 'var(--text-secondary)', fontWeight: 500 }}>
              📎 Attach resume — {selectedCandidate.fullName}
            </span>
            {attachResume && !selectedCandidate.resumeName && (
              <span style={{ fontSize: 11, color: isDark ? '#f87171' : '#dc2626', marginLeft: 4 }}>⚠ No resume on file</span>
            )}
            {attachResume && selectedCandidate.resumeName && (
              <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>{selectedCandidate.resumeName}</span>
            )}
          </label>
        )}

        <Button variant="success" onClick={handleSend} disabled={isDeveloper || sending || toEmails.length === 0 || !form.subject || !form.body || !allRequiredFilled} loading={sending} loadingText="Sending..." title={isDeveloper ? 'Disabled for Developer role' : ''} style={{ width: '100%' }}>✉ Send to Client</Button>
      </div>

      {/* Preview panel */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 style={{ color: 'var(--text-primary)' }} className="font-medium">Preview</h3>
          {preview && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>✏ Click anywhere to edit</span>}
        </div>
        {!preview ? (
          <div style={{ background: 'var(--bg-raised)', border: '1px dashed var(--border-subtle)', color: 'var(--text-muted)' }} className="rounded-xl flex items-center justify-center h-64 text-sm text-center px-6">
            Select candidate, requirement and client contact, then click "Preview email"
          </div>
        ) : (
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)' }} className="rounded-xl overflow-hidden">
            <div className="px-4 pt-3 pb-1 space-y-0.5">
              <EmailTagInput label="To" emails={toEmails} onChange={setToEmails} isDark={isDark} />
              <EmailTagInput label="Cc" emails={ccEmails} onChange={setCcEmails} isDark={isDark} />
              <div style={{ borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
                <span style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 500, width: 28, flexShrink: 0 }}>Sub</span>
                <input
                  value={form.subject}
                  onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                  style={{ background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: 12, flex: 1, padding: '2px 0' }}
                  placeholder="Enter subject…"
                />
              </div>
              {selectedCandidate && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0' }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 500, width: 28, flexShrink: 0 }}>Re</span>
                  <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>Candidate — {selectedCandidate.fullName}</span>
                </div>
              )}
              {attachResume && selectedCandidate?.resumeName && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0' }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 500, width: 28, flexShrink: 0 }}>📎</span>
                  <span style={{ color: isDark ? '#6ee7b7' : '#047857', fontSize: 12 }}>{selectedCandidate.resumeName}</span>
                </div>
              )}
            </div>
            <div
              ref={clientBodyRef}
              contentEditable
              suppressContentEditableWarning
              onInput={e => { const v = e.currentTarget.innerHTML || e.currentTarget.innerText || ''; setForm(p => ({ ...p, body: v })); }}
              className="px-4 py-4 max-h-[600px] overflow-y-auto break-words"
              style={{
                outline: 'none', cursor: 'text', minHeight: 120, color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.6,
                borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}`,
                wordBreak: 'break-word', overflowWrap: 'anywhere',
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Batch Send to Client sub-tab ──────────────────────────────────────────────
// Builds an HTML table of multiple candidates and sends to a client contact.
function BatchSendToClientTab({ templates: allTemplates, candidates, requirements }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { isDeveloper, user } = useAuth();
  // Only show batch templates (client-facing) — {{candidates_table}} or a
  // {{#candidates}} loop block.
  const templates = allTemplates.filter(t =>
    (t.targetAudience === 'client' || t.targetAudience === 'both') && hasBatchTableVar(t)
  );
  const [form, setForm] = useState({ templateId: '', requirementId: '', clientContact: null, subject: '', body: '' });
  const [selectedIds,          setSelectedIds]          = useState([]);
  const [extraVars,            setExtraVars]            = useState({});
  const [htmlBody,             setHtmlBody]             = useState('');
  const [sending,              setSending]              = useState(false);
  const [preview,              setPreview]              = useState(false);
  const [requirementCandidates, setRequirementCandidates] = useState([]);
  const [loadingCands,         setLoadingCands]         = useState(false);
  // { [candidateId]: ISOString } of the last time each candidate was included
  // in a batch client email FOR THE CURRENTLY SELECTED REQUIREMENT. Drives the
  // "Sent" indicator and the auto-uncheck / skip-in-Select-all behavior below.
  const [alreadySentMap,       setAlreadySentMap]       = useState({});
  const [toEmails,             setToEmails]             = useState([]);
  const [ccEmails,             setCcEmails]             = useState([]);
  const [attachCVs,            setAttachCVs]            = useState(true);
  const batchBodyRef = React.useRef(null);

  const selectedTemplate    = templates.find(t => t.id === form.templateId);
  const requiredVars        = getRequiredVars(selectedTemplate);
  const allRequiredFilled   = requiredVars.every(v => (extraVars[v] || '').trim() !== '');

  const selectedRequirement = requirements.find(r => r.id === form.requirementId);

  // When the selected requirement pins a batch template (and it exists), the
  // template is LOCKED — the picker is disabled and can't be changed from here.
  const lockedTemplateId =
    (selectedRequirement?.batchTemplateId && templates.some(t => t.id === selectedRequirement.batchTemplateId))
      ? selectedRequirement.batchTemplateId
      : null;

  // Options for the dropdown: hide templates marked hideFromBatch, but always
  // keep the currently-selected one and the requirement-pinned one visible.
  const keepIds = new Set([form.templateId, selectedRequirement?.batchTemplateId].filter(Boolean));
  const visibleTemplates = templates.filter(t => !t.hideFromBatch || keepIds.has(t.id));

  // Default batch template = the original "Candidate Profiles – Batch Submission"
  // (matched by name, dash-type-insensitive), else the first non-hidden format
  // whose name has NO trailing number (space OR hyphen/dash separated), else the
  // first non-hidden. Computed from `templates` only so it stays stable.
  const normName   = (s) => String(s || '').toLowerCase().replace(/[–—]/g, '-').replace(/\s+/g, ' ').trim();
  const isNumbered = (s) => /[\s\-–]+\d+\s*$/.test(String(s || ''));
  const CANON_BATCH_NAME = 'candidate profiles - batch submission';
  const defaultTemplateId =
    (templates.find(t => !t.hideFromBatch && normName(t.name) === CANON_BATCH_NAME)
      || templates.find(t => !t.hideFromBatch && !isNumbered(t.name))
      || templates.find(t => !t.hideFromBatch)
      || templates[0])?.id || '';

  // Selection: requirement pins a template → use it (locked); otherwise fall back
  // to the default template above. Runs on requirement change and when templates
  // finish loading; a manual pick (when nothing is pinned) survives between
  // requirement changes since neither dep changes then.
  useEffect(() => {
    const target = lockedTemplateId || defaultTemplateId;
    if (target) setForm(p => (p.templateId === target ? p : { ...p, templateId: target }));
  }, [form.requirementId, templates.length]);

  // Auto-populate To when client contact changes. Swaps the previous
  // auto-added contact email for the new one (preserving user-typed emails).
  // Same snapshot-before-update pattern as the CC effect.
  const autoToRef = useRef(null);
  useEffect(() => {
    const newEmail = form.clientContact?.email || null;
    const oldEmail = autoToRef.current;
    autoToRef.current = newEmail;

    setToEmails(prev => {
      const userTyped = oldEmail
        ? prev.filter(e => e.toLowerCase() !== oldEmail.toLowerCase())
        : [...prev];
      if (newEmail && !userTyped.some(e => e.toLowerCase() === newEmail.toLowerCase())) {
        return [newEmail, ...userTyped];
      }
      return userTyped;
    });
  }, [form.clientContact?.email]);

  // Auto-merge the selected requirement's defaultCcEmail into the CC tag
  // input. When the requirement is SWITCHED, the previous requirement's
  // auto-CCs are removed first so emails don't pile up across selections.
  // User-typed emails are preserved.
  const autoCcRef = useRef([]);
  useEffect(() => {
    const raw = selectedRequirement?.defaultCcEmail || '';
    const newDefaults = raw.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
    // Snapshot old defaults BEFORE updating the ref — see SendToClientTab
    // comment for why this matters (closure/timing of queued setState).
    const oldDefaults = autoCcRef.current;
    autoCcRef.current = newDefaults;

    setCcEmails(prev => {
      const userTyped = prev.filter(e => !oldDefaults.includes(e.toLowerCase()));
      const seen = new Set(userTyped.map(e => e.toLowerCase()));
      const merged = [...userTyped];
      for (const d of newDefaults) {
        if (!seen.has(d)) {
          merged.push(d);
          seen.add(d);
        }
      }
      return merged;
    });
  }, [selectedRequirement?.id, selectedRequirement?.defaultCcEmail]);

  // Fetch candidates via submissions when a requirement is selected.
  // In parallel, fetch the "already sent to this client for this requirement"
  // map so we can render a badge and auto-drop those from selectedIds.
  //
  // Cancellation guard: rapid requirement switches (A→B→A) can resolve
  // out-of-order and repopulate state with a stale requirement's data.
  // A `cancelled` closure flag on the effect's cleanup path short-circuits
  // stale resolutions.
  useEffect(() => {
    if (!form.requirementId) {
      setRequirementCandidates([]);
      setAlreadySentMap({});
      return;
    }
    let cancelled = false;
    setLoadingCands(true);
    Promise.allSettled([
      submissionAPI.getAll({ requirementId: form.requirementId, limit: 500 }),
      emailAPI.getSentCandidates({ requirementId: form.requirementId }),
    ])
      .then(([submissionsRes, sentRes]) => {
        if (cancelled) return;
        if (submissionsRes.status === 'rejected') toast.error('Failed to load candidates for this requirement.');
        const data = submissionsRes.status === 'fulfilled' ? submissionsRes.value.data : { submissions: [] };
        const seen = new Set();
        const list = [];
        for (const sub of (data.submissions || [])) {
          // Deleted candidates can't be pitched (the send would be refused).
          if (!sub.candidate || !sub.candidateId || sub.candidate.isDeleted) continue;
          if (!seen.has(sub.candidateId)) {
            seen.add(sub.candidateId);
            list.push({
              id:                      sub.candidateId,
              fullName:                sub.candidate?.fullName            || '—',
              email:                   sub.candidate?.email               || '',
              phone:                   sub.candidate?.phone               || '',
              experience:              sub.candidate?.experience          ?? null,
              relevantExperience:      sub.candidate?.relevantExperience  || '',
              skills:                  sub.candidate?.skills              || [],
              noticePeriod:            sub.candidate?.noticePeriod        ?? null,
              servingNoticePeriod:     sub.candidate?.servingNoticePeriod ?? false,
              offerInHand:             sub.candidate?.offerInHand         ?? false,
              location:                sub.candidate?.location            || '',
              preferredLocation:       sub.candidate?.preferredLocation   || '',
              linkedInUrl:             sub.candidate?.linkedInUrl         || '',
              availabilityToInterview: sub.candidate?.availabilityToInterview || '',
              experienceSummary:       sub.candidate?.experienceSummary   || '',
              noticePeriodNotes:       sub.candidate?.noticePeriodNotes   || '',
              currentRole:             sub.candidate?.currentRole         || '',
              resumeName:              sub.candidate?.resumeName          || null,
              notes:                   sub.candidate?.notes               || '',
              clientNotes:             sub.clientNotes || sub.notes        || '',
              overrideBillingCost:     sub.overrideBillingCost     ?? null,
              overrideBillingCurrency: sub.overrideBillingCurrency ?? null,
              overrideBillingType:     sub.overrideBillingType     ?? null,
              status:                  sub.status,
            });
          }
        }
        setRequirementCandidates(list);

        const sentMap = sentRes.status === 'fulfilled' ? (sentRes.value.data?.lastSentAt || {}) : {};
        setAlreadySentMap(sentMap);
        // Drop any already-sent IDs from selectedIds — if a recruiter had
        // pre-picked candidates and then switched requirements, we don't want
        // to silently keep sent-to-this-req profiles queued.
        setSelectedIds(prev => prev.filter(id => !sentMap[id]));
      })
      .catch(() => {
        if (cancelled) return;
        setRequirementCandidates([]);
        setAlreadySentMap({});
      })
      .finally(() => { if (!cancelled) setLoadingCands(false); });

    return () => { cancelled = true; };
  }, [form.requirementId]);

  const clientContacts = (() => {
    if (!selectedRequirement) return [];
    const contacts = selectedRequirement.contacts;
    if (Array.isArray(contacts) && contacts.length > 0) return contacts;
    if (selectedRequirement.client?.contactEmail)
      return [{ name: selectedRequirement.client.clientName || 'Client', email: selectedRequirement.client.contactEmail }];
    return [];
  })();

  // Format billing amount for a single candidate — override takes priority over requirement default
  const resolveBilling = (candidate, req) => {
    const cost     = candidate.overrideBillingCost     ?? req?.billingCost;
    const currency = candidate.overrideBillingCurrency ?? req?.billingCurrency ?? 'USD';
    const type     = candidate.overrideBillingType     ?? req?.billingType;
    if (!cost) return '—';
    const sym    = { USD: '$', EUR: '€', GBP: '£', INR: '₹', AED: 'AED ', SGD: 'S$' }[currency] || `${currency} `;
    const suffix = type === 'Hourly' ? '/hr' : type === 'Monthly' ? '/mo' : type === 'Percentage' ? '%' : '';
    const amount = type === 'Percentage' ? Number(cost) : `${sym}${Number(cost).toLocaleString()}`;
    return `${amount}${suffix}`;
  };

  const buildTable = (candidateList, req) => {
    const th = (txt) => `<th style="padding:9px 12px;border:1px solid #1e40af;text-align:left;white-space:nowrap;color:#ffffff;">${txt}</th>`;
    const td = (txt, wrap) => `<td style="padding:8px 12px;border:1px solid #cbd5e1;${wrap ? 'white-space:normal;max-width:220px;' : 'white-space:nowrap;'}color:#1e293b;">${txt ?? '—'}</td>`;
    const rows = candidateList.map((c, i) => `
      <tr style="background:${i % 2 === 0 ? '#f0f9ff' : '#ffffff'};">
        ${td(c.fullName)}
        ${td(req?.jobLocation || req?.location || '—')}
        ${td(c.status || '—')}
        ${td(c.phone || '—')}
        ${td(c.email)}
        ${td(c.experience != null ? (fmtExp(c.experience) || '0 yrs') : '—')}
        ${td(relevantExpText(c) || '—', true)}
        ${td(noticeText(c, { short: true }) || '—', true)}
        ${td(c.offerInHand ? 'Yes' : 'No')}
        ${td(resolveBilling(c, req))}
        ${td(c.location || '—')}
        ${td(c.preferredLocation || '—')}
        ${td(c.clientNotes || '—', true)}
      </tr>`).join('');
    return `<table style="border-collapse:collapse;width:100%;font-family:Arial,sans-serif;font-size:13px;margin:16px 0;">
  <thead>
    <tr style="background:#1a56db;">
      ${th('Name')}${th('Job Location')}${th('Status')}${th('Contact No.')}${th('Email')}
      ${th('Total Exp')}${th('Relevant Exp')}${th('Notice Period (Days)')}
      ${th('Offer In Hand')}${th('Bill Rate')}${th('Current Location')}${th('Preferred Location')}${th('Notes')}
    </tr>
  </thead>
  <tbody>${rows}</tbody>
</table>`;
  };

  // Per-candidate fields available INSIDE a {{#candidates}}…{{/candidates}} loop
  // block. The template author writes the HTML (a <tr> for horizontal, a whole
  // <table> for vertical, anything) using these {{placeholders}}; the engine
  // repeats the block for each selected candidate. This is what lets a new
  // format be defined purely at the template level — no per-layout code.
  const perCandidateVars = (c) => {
    const notice = noticeText(c);
    const linkedinLink = (() => {
      const u = String(c.linkedInUrl || '').trim();
      if (!u) return '';
      const href = /^https?:\/\//i.test(u) ? u : `https://${u}`;
      return `<a href="${href}" style="color:#2563eb;" target="_blank" rel="noopener">${u}</a>`;
    })();
    return {
      name:                      c.fullName || '',
      phone:                     c.phone || '',
      email:                     c.email || '',
      linkedin:                  linkedinLink,          // clickable link
      linkedin_url:              c.linkedInUrl || '',   // raw text
      location:                  c.location || '',
      preferred_location:        c.preferredLocation || '',
      experience:                c.experience != null ? (fmtExp(c.experience) || '') : '',
      relevant_experience:       relevantExpText(c),
      notice_period:             notice,
      availability_to_join:      notice,
      availability_to_interview: c.availabilityToInterview || '',
      experience_summary:        c.experienceSummary || '',
      notice_period_notes:       c.noticePeriodNotes || '',
      current_role:              c.currentRole || '',
      current_company:           c.currentCompany || '',
      skills:                    Array.isArray(c.skills) ? c.skills.join(', ') : (c.skills || ''),
      bill_rate:                 resolveBilling(c, selectedRequirement) || '',
      status:                    c.status || '',
      offer_in_hand:             c.offerInHand ? 'Yes' : 'No',
    };
  };

  // Expand every {{#candidates}}…{{/candidates}} block: repeat its inner HTML
  // for each selected candidate with per-candidate {{fields}} substituted.
  const expandCandidateLoops = (body, candidateList) =>
    String(body || '').replace(/\{\{#candidates\}\}([\s\S]*?)\{\{\/candidates\}\}/g, (_, block) =>
      candidateList.map((c) => {
        const cv = perCandidateVars(c);
        return block.replace(/\{\{(\w+)\}\}/g, (_, k) => (k in cv ? cv[k] : ''));
      }).join('')
    );

  const handleBuildPreview = () => {
    const chosenCandidates = requirementCandidates.filter(c => selectedIds.includes(c.id));
    if (chosenCandidates.length === 0) return toast.error('Select at least one candidate.');
    if (!form.clientContact) return toast.error('Select a client contact.');
    if (!form.subject) return toast.error('Subject is required.');

    const table = buildTable(chosenCandidates, selectedRequirement);
    const template = templates.find(t => t.id === form.templateId);

    // Simple variable replacement (client-side only for batch mode)
    const vars = {
      client_name:    form.clientContact?.name || '',
      job_title:      selectedRequirement?.jobTitle || '',
      company_name:   selectedRequirement?.client?.clientName || '',
      billing_cost:   resolveBilling({ overrideBillingCost: null }, selectedRequirement),
      candidates_table: table,
      sender_name:    user?.name || '',
      ...extraVars,   // user-supplied required vars override/extend auto-filled ones
    };
    const rawBody = template?.body || form.body || '';
    // 1) Expand any {{#candidates}} loop blocks (template-defined per-candidate
    //    layout), then 2) substitute the outer {{vars}}.
    const looped = expandCandidateLoops(rawBody, chosenCandidates);
    const interpolated = looped.replace(/\{\{(\w+)\}\}/g, (_, k) => vars[k] ?? `{{${k}}}`);
    // Wrap plain-text lines as <p> but leave HTML blocks (like the table) intact
    const withHtml = interpolated.split('\n').map(line =>
      line.trim().startsWith('<') ? line
        : `<p style="margin:4px 0;font-family:Arial,sans-serif;font-size:14px;color:#1e293b;">${line || '&nbsp;'}</p>`
    ).join('');
    setHtmlBody(withHtml);
    setPreview(true);
    // Set contentEditable body directly (one-time) so cursor stays put during editing
    setTimeout(() => { if (batchBodyRef.current) batchBodyRef.current.innerHTML = withHtml; }, 0);
  };

  const handleSend = async () => {
    if (toEmails.length === 0 || !form.subject || !htmlBody) return toast.error('Complete all required fields first.');
    if (attachCVs) {
      const noCv = requirementCandidates.filter(c => selectedIds.includes(c.id) && !c.resumeName);
      if (noCv.length) {
        return toast.error(`No resume on file for: ${noCv.map(c => c.fullName).join(', ')}. Upload it first (Candidates → Edit) or unselect them.`);
      }
    }
    setSending(true);
    try {
      const { data } = await emailAPI.sendToClient({
        templateId:    form.templateId || undefined,
        requirementId: form.requirementId || undefined,
        toEmail:       toEmails[0],
        toName:        form.clientContact?.name || '',
        to:            toEmails,
        cc:            ccEmails.length > 0 ? ccEmails : undefined,
        subject:       form.subject,
        body:          htmlBody,
        attachResume:  attachCVs,
        candidateIds:  attachCVs ? selectedIds : undefined,
      });
      toast.success(`Batch email sent to ${toEmails.join(', ')}!`);
      // Show attachment summary if CVs were requested
      if (attachCVs && data?.attachLog) {
        const { attached, skipped } = data.attachLog;
        if (attached?.length)
          toast.success(`📎 ${attached.length} CV${attached.length !== 1 ? 's' : ''} attached: ${attached.map(a => a.name).join(', ')}`);
        if (skipped?.length)
          toast.error(`⚠ ${skipped.length} CV${skipped.length !== 1 ? 's' : ''} skipped (no resume uploaded): ${skipped.map(s => s.name || s.id).join(', ')}`);
      }
      setForm({ templateId: '', requirementId: '', clientContact: null, subject: '', body: '' });
      setSelectedIds([]); setHtmlBody(''); setPreview(false); setToEmails([]); setCcEmails([]); setAttachCVs(true);
    } catch (err) { toast.error(err?.response?.data?.message || 'Failed to send email.'); }
    finally { setSending(false); }
  };

  const toggleCandidate = (id) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="space-y-4">
        <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-4 py-3">
          <span className="text-xl">📋</span>
          <p className="text-emerald-300 text-sm font-medium">Batch Mode — select multiple candidates and send a profile table to the client</p>
        </div>

        {/* Template — searchable; only batch templates ({{candidates_table}}),
            with hidden ones filtered out (except a pinned/selected one). */}
        <SearchableSelect
          label="Batch Template"
          value={form.templateId}
          disabled={!!lockedTemplateId}
          onChange={v => { if (lockedTemplateId) return; setForm(p => ({ ...p, templateId: v, body: '' })); setExtraVars({}); setPreview(false); setHtmlBody(''); }}
          placeholder="— Search & select a template —"
          options={visibleTemplates.map(t => ({ value: t.id, label: t.name, sub: t.subject || '' }))}
        />
        {lockedTemplateId && (
          <p className="text-xs -mt-2" style={{ color: 'var(--text-muted)' }}>
            🔒 Format locked by the selected requirement. To use a different one, change the requirement's “Batch Email Format”.
          </p>
        )}
        {templates.length === 0 && (
          <p className="text-amber-400 text-xs -mt-2">⚠ No batch templates found. Run the seeder to create the "Candidate Profiles – Batch Submission" template.</p>
        )}

        {/* Requirement */}
        <SearchableSelect
          label="Requirement * (loads candidates & client contacts)"
          value={form.requirementId}
          onChange={v => {
            const req = requirements.find(r => r.id === v);
            setForm(p => ({ ...p, requirementId: v, clientContact: null,
              subject: req ? `Submission || ${req.jobTitle}${req.client?.clientName ? ' || ' + req.client.clientName : ''}` : '' }));
            setSelectedIds([]); setPreview(false); setHtmlBody(''); setRequirementCandidates([]);
          }}
          placeholder="— Select a requirement —"
          options={requirements.map(r => ({
            value: r.id,
            label: r.jobTitle,
            sub:   r.client?.clientName || '',
          }))}
        />

        {/* Subject */}
        <TextField label="Email Subject *" value={form.subject}
          onChange={v => setForm(p => ({ ...p, subject: v }))}
          placeholder={`Candidate Profiles for Review – ${selectedRequirement?.jobTitle || 'Job Title'}`} />

        {/* Candidate checklist */}
        {form.requirementId && (
          <div style={{ background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: 16 }} className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                <p style={{ color: 'var(--text-secondary)', fontSize: 12, fontWeight: 500 }}>Select Candidates *</p>
                {(() => {
                  const alreadySentCount = requirementCandidates.filter(c => alreadySentMap[c.id]).length;
                  return alreadySentCount > 0 && (
                    <span style={{ color: isDark ? '#fbbf24' : '#b45309', fontSize: 11 }}>
                      · {alreadySentCount} already sent, skipped on Select all
                    </span>
                  );
                })()}
              </div>
              {requirementCandidates.length > 0 && (
                <div className="flex gap-3 text-xs">
                  <Button variant="ghost" size="xs" onClick={() => setSelectedIds(requirementCandidates.filter(c => !alreadySentMap[c.id]).map(c => c.id))}>Select all</Button>
                  <Button variant="ghost" size="xs" onClick={() => setSelectedIds([])}>Clear</Button>
                </div>
              )}
            </div>
            {loadingCands
              ? <p style={{ color: 'var(--text-muted)' }} className="text-xs">Loading candidates…</p>
              : requirementCandidates.length === 0
              ? <p className="text-amber-400 text-xs">⚠ No submissions found for this requirement.</p>
              : requirementCandidates.map(c => {
                const statusStyle = {
                  Shortlisted: { background: isDark ? 'rgba(59,130,246,0.15)' : '#eff6ff', color: isDark ? '#93c5fd' : '#1d4ed8' },
                  Interview:   { background: isDark ? 'rgba(139,92,246,0.15)' : '#f5f3ff', color: isDark ? '#c4b5fd' : '#6d28d9' },
                  Hired:       { background: isDark ? 'rgba(16,185,129,0.15)' : '#ecfdf5', color: isDark ? '#6ee7b7' : '#047857' },
                }[c.status] || { background: 'var(--bg-card)', color: 'var(--text-muted)' };
                const alreadySentAt = alreadySentMap[c.id];
                const isSelected    = selectedIds.includes(c.id);
                // Muted amber pill — signals "already pitched, review before re-sending"
                // without shouting error-red.
                const sentBadgeStyle = {
                  background: isDark ? 'rgba(245,158,11,0.18)' : '#fef3c7',
                  color:      isDark ? '#fbbf24' : '#b45309',
                  border:     `1px solid ${isDark ? 'rgba(245,158,11,0.35)' : '#fcd34d'}`,
                  marginLeft: 8,
                  fontSize:   11,
                  padding:    '2px 6px',
                  borderRadius: 4,
                  fontWeight: 500,
                };
                // Dim unselected already-sent rows so the eye skips them.
                const cardStyle = isSelected
                  ? { border: '1px solid #10b981', background: isDark ? 'rgba(16,185,129,0.1)' : '#ecfdf5', borderRadius: 12, padding: 12, display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }
                  : alreadySentAt
                    ? { border: `1px dashed ${isDark ? 'rgba(245,158,11,0.35)' : '#fcd34d'}`, background: 'var(--bg-card)', borderRadius: 12, padding: 12, display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer', opacity: 0.72 }
                    : { border: '1px solid var(--border-subtle)', background: 'var(--bg-card)', borderRadius: 12, padding: 12, display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' };
                return (
                <label key={c.id} style={cardStyle}>
                  <input type="checkbox" checked={isSelected} onChange={() => toggleCandidate(c.id)} className="mt-0.5 accent-emerald-500" />
                  <div className="flex-1 min-w-0">
                    <p style={{ color: 'var(--text-primary)' }} className="text-sm font-medium">{c.fullName}
                      <span style={{ ...statusStyle, marginLeft: 8, fontSize: 11, padding: '2px 6px', borderRadius: 4 }}>{c.status}</span>
                      {alreadySentAt && (() => {
                        const d = new Date(alreadySentAt);
                        const when = isNaN(d.getTime()) ? '' : ` on ${d.toLocaleString()}`;
                        return (
                          <span
                            style={sentBadgeStyle}
                            title={`Already sent to this client for this requirement${when}`}
                          >
                            ✉ Sent
                          </span>
                        );
                      })()}
                      {!c.resumeName && (
                        <span
                          style={{ marginLeft: 8, fontSize: 11, padding: '2px 6px', borderRadius: 4, fontWeight: 500, color: isDark ? '#fca5a5' : '#b91c1c', background: isDark ? 'rgba(239,68,68,0.15)' : '#fee2e2' }}
                          title="No resume on file — its CV can't be attached, so it can't be sent with CVs"
                        >
                          ⚠ No resume
                        </span>
                      )}
                    </p>
                    <p style={{ color: 'var(--text-secondary)' }} className="text-xs mt-0.5">{c.email} · Total exp: {fmtExp(c.experience) || '0 yrs'}{c.location ? ` · ${c.location}` : ''}</p>
                    {c.relevantExperience && <p style={{ color: 'var(--text-muted)' }} className="text-xs mt-0.5 truncate">Relevant exp: {fmtRelevantExp(c.relevantExperience)}</p>}
                  </div>
                </label>
                );
              })
            }
            {selectedIds.length > 0 && (
              <p className="text-emerald-400 text-xs font-medium">{selectedIds.length} candidate{selectedIds.length > 1 ? 's' : ''} selected for the table</p>
            )}
          </div>
        )}

        {/* Attach CVs toggle — visible once candidates are selected */}
        {selectedIds.length > 0 && (
          <label style={{
            background: attachCVs ? (isDark ? 'rgba(16,185,129,0.08)' : '#f0fdf4') : 'var(--bg-raised)',
            border: `1px solid ${attachCVs ? (isDark ? 'rgba(16,185,129,0.3)' : '#a7f3d0') : 'var(--border-subtle)'}`,
            borderRadius: 12, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
          }}>
            <input
              type="checkbox"
              checked={attachCVs}
              onChange={e => setAttachCVs(e.target.checked)}
              className="accent-emerald-500"
            />
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: 13, color: attachCVs ? (isDark ? '#6ee7b7' : '#047857') : 'var(--text-secondary)', fontWeight: 500 }}>
                📎 Attach CVs to email
              </span>
              {attachCVs && (
                <p style={{ fontSize: 11, color: isDark ? '#6ee7b7' : '#047857', marginTop: 2 }}>
                  {selectedIds.length} CV{selectedIds.length !== 1 ? 's' : ''} will be attached — every selected candidate needs a resume on file
                </p>
              )}
            </div>
          </label>
        )}

        {/* Client contact picker */}
        {selectedRequirement && clientContacts.length > 0 && (
          <div>
            <label style={{ color: 'var(--text-muted)', fontSize: 12, display: 'block', marginBottom: 8 }}>Send to (Client Contact) *</label>
            <div className="space-y-2">
              {clientContacts.map((contact, i) => (
                <label key={i}
                  style={form.clientContact?.email === contact.email
                    ? { border: '1px solid #10b981', background: isDark ? 'rgba(16,185,129,0.1)' : '#ecfdf5', borderRadius: 12, padding: 12, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }
                    : { border: '1px solid var(--border-subtle)', background: 'var(--bg-raised)', borderRadius: 12, padding: 12, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
                  <input type="radio" name="batchClientContact" checked={form.clientContact?.email === contact.email}
                    onChange={() => setForm(p => ({ ...p, clientContact: contact }))} className="accent-emerald-500" />
                  <div className="flex-1 min-w-0">
                    <p style={{ color: 'var(--text-primary)' }} className="text-sm font-medium">{contact.name || 'Unnamed Contact'}</p>
                    <p style={{ color: 'var(--text-secondary)' }} className="text-xs">{contact.email}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Auto-detected required variables for batch template */}
        {requiredVars.length > 0 && (
          <div style={{ background: 'var(--bg-raised)', border: '1px solid rgba(245,158,11,0.35)', borderRadius: 12, padding: 16 }} className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-amber-400 text-sm">⚠</span>
              <p className="text-xs text-amber-400 font-medium">Required fields — fill all before sending</p>
            </div>
            <p style={{ color: 'var(--text-muted)' }} className="text-xs">These variables are in the template and are not auto-filled. They will be injected into every email sent.</p>
            <div className="space-y-2">
              {requiredVars.map(v => (
                <div key={v}>
                  <label style={{ color: 'var(--text-secondary)', fontSize: 12, display: 'block', marginBottom: 4, fontWeight: 500 }}>{varLabel(v)}</label>
                  <input
                    value={extraVars[v] || ''}
                    onChange={e => setExtraVars(p => ({ ...p, [v]: e.target.value }))}
                    placeholder={`Enter ${v.replace(/_/g, ' ')}`}
                    style={{ background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid ' + ((extraVars[v] || '').trim() ? 'rgba(34,197,94,0.5)' : 'rgba(245,158,11,0.4)') }}
                    className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none transition-colors"
                  />
                </div>
              ))}
            </div>
            {/* Progress bar */}
            <div className="flex items-center gap-2 pt-1">
              <div style={{ background: 'var(--bg-raised)' }} className="flex-1 rounded-full h-1">
                <div
                  className="bg-amber-400 h-1 rounded-full transition-all"
                  style={{ width: `${requiredVars.length ? (requiredVars.filter(v => (extraVars[v] || '').trim()).length / requiredVars.length) * 100 : 0}%` }}
                />
              </div>
              <span style={{ color: 'var(--text-muted)' }} className="text-xs">
                {requiredVars.filter(v => (extraVars[v] || '').trim()).length}/{requiredVars.length} filled
              </span>
            </div>
          </div>
        )}

        {requiredVars.length > 0 && !allRequiredFilled && form.templateId && (
          <p className="text-xs text-amber-400 text-center">Fill in all required fields above to enable sending</p>
        )}

        <Button variant="outline" onClick={handleBuildPreview} disabled={isDeveloper || selectedIds.length === 0 || !form.clientContact || !form.subject || !allRequiredFilled} title={isDeveloper ? 'Disabled for Developer role' : ''} style={{ width: '100%' }}>📊 Build Table &amp; Preview</Button>
        {preview && (
          <Button variant="success" onClick={handleSend} disabled={isDeveloper || sending || !allRequiredFilled || toEmails.length === 0} loading={sending} loadingText="Sending..." title={isDeveloper ? 'Disabled for Developer role' : ''} style={{ width: '100%' }}>{`✉ Send Batch Email to ${toEmails[0] || 'Client'}`}</Button>
        )}
      </div>

      {/* HTML Preview panel */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 style={{ color: 'var(--text-primary)' }} className="font-medium">Email Preview</h3>
          {preview && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>✏ Click anywhere to edit</span>}
        </div>
        {!preview ? (
          <div style={{ background: 'var(--bg-raised)', border: '1px dashed var(--border-subtle)', color: 'var(--text-muted)' }} className="rounded-xl flex items-center justify-center h-64 text-sm text-center px-6">
            Select candidates, a client contact and subject, then click "Build Table &amp; Preview"
          </div>
        ) : (
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)' }} className="rounded-xl">
            <div className="px-4 pt-3 pb-1 space-y-0.5">
              <EmailTagInput label="To" emails={toEmails} onChange={setToEmails} isDark={isDark} />
              <EmailTagInput label="Cc" emails={ccEmails} onChange={setCcEmails} isDark={isDark} />
              <div style={{ borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
                <span style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 500, width: 28, flexShrink: 0 }}>Sub</span>
                <input
                  value={form.subject}
                  onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                  style={{ background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: 12, flex: 1, padding: '2px 0' }}
                  placeholder="Enter subject…"
                />
              </div>
            </div>
            <div
              ref={batchBodyRef}
              contentEditable
              suppressContentEditableWarning
              onInput={e => { const v = e.currentTarget.innerHTML || ''; setHtmlBody(v); }}
              className="p-4 overflow-auto max-h-[540px]"
              style={{
                outline: 'none', cursor: 'text', minHeight: 120,
                borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}`,
              }}
            />
            {attachCVs && selectedIds.length > 0 && (
              <div style={{ borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}`, padding: '8px 16px' }}>
                <p style={{ fontSize: 11, color: isDark ? '#6ee7b7' : '#047857' }}>
                  📎 {selectedIds.length} CV{selectedIds.length !== 1 ? 's' : ''} will be attached
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Compose Tab (wrapper with sub-tabs) ───────────────────────────────────────
function ComposeTab({ prefill }) {
  const { isRecruiter } = useAuth();
  const recruiter = isRecruiter();

  // Recruiters always land on candidate tab; managers can deep-link to client
  const [subTab, setSubTab]             = useState(
    !recruiter && prefill?.compose === 'client' ? 'client' : 'candidate'
  );
  const [templates, setTemplates]       = useState([]);
  const [candidates, setCandidates]     = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading]           = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [tRes, cRes, rRes] = await Promise.all([
          emailAPI.getTemplates(),
          candidateAPI.dropdown(),
          requirementAPI.dropdown(),
        ]);
        setTemplates(tRes.data.templates || []);
        setCandidates(cRes.data.candidates || []);
        setRequirements((rRes.data.requirements || []).filter(r => r.status !== 'Closed'));
      } catch { toast.error('Failed to load data.'); }
      finally { setLoading(false); }
    };
    load();
  }, []); // eslint-disable-line

  if (loading) return <div style={{ color: 'var(--text-muted)' }} className="text-center py-16 text-sm">Loading...</div>;

  return (
    <div className="space-y-5">
      {/* Sub-tab switcher */}
      <div style={{ background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: 4, display: 'flex', flexWrap: 'wrap' }}>
        <Button onClick={() => setSubTab('candidate')} variant={subTab === 'candidate' ? 'primary' : 'ghost'} size="sm">✉ Send to Candidate</Button>
        {/* Client-facing tabs hidden for Recruiters */}
        {!recruiter && (
          <>
            <Button onClick={() => setSubTab('client')} variant={subTab === 'client' ? 'success' : 'ghost'} size="sm">🏢 Send to Client</Button>
            <Button onClick={() => setSubTab('batch')} variant={subTab === 'batch' ? 'teal' : 'ghost'} size="sm">📋 Batch to Client</Button>
          </>
        )}
      </div>
      <p style={{ color: 'var(--text-muted)' }} className="text-xs">
        {subTab === 'candidate' && 'Send emails to one or multiple candidates using the toggles inside.'}
        {subTab === 'client'    && 'Forward a single candidate profile and requirement details to client contacts for review.'}
        {subTab === 'batch'     && 'Select multiple candidates and send their profiles as a formatted table to the client.'}
      </p>
      {subTab === 'candidate' && (
        <SendToCandidateTab templates={templates} candidates={candidates} requirements={requirements}
          prefillRequirementId={prefill?.requirement?.id}
          prefillCandidateId={prefill?.candidate?.id} />
      )}
      {!recruiter && subTab === 'client' && (
        <SendToClientTab templates={templates} candidates={candidates} requirements={requirements}
          prefillCandidateId={prefill?.candidate?.id}
          prefillRequirementId={prefill?.requirement?.id}
          prefillSource={prefill?.fromSource} />
      )}
      {!recruiter && subTab === 'batch' && (
        <BatchSendToClientTab templates={templates} candidates={candidates} requirements={requirements} />
      )}
    </div>
  );
}

// ── Shared helpers for logs ───────────────────────────────────────────────────
const statusBadgeStyle = (s, isDark) => ({
  sent:    { background: isDark ? 'rgba(16,185,129,0.15)' : '#ecfdf5', color: isDark ? '#6ee7b7' : '#047857', border: '1px solid ' + (isDark ? 'rgba(16,185,129,0.3)' : '#a7f3d0') },
  failed:  { background: isDark ? 'rgba(239,68,68,0.15)' : '#fef2f2', color: isDark ? '#fca5a5' : '#b91c1c', border: '1px solid ' + (isDark ? 'rgba(239,68,68,0.3)' : '#fecaca') },
  pending: { background: isDark ? 'rgba(245,158,11,0.15)' : '#fffbeb', color: isDark ? '#fcd34d' : '#b45309', border: '1px solid ' + (isDark ? 'rgba(245,158,11,0.3)' : '#fde68a') },
}[s] || {});

function LogViewModal({ log, onClose }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  if (!log) return null;
  return (
    <Modal title="Email details" onClose={onClose}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          <div><p style={{ color: 'var(--text-muted)', marginBottom: 2 }}>To</p><p style={{ color: 'var(--text-primary)' }} className="break-words">{log.toEmail}</p></div>
          <div><p style={{ color: 'var(--text-muted)', marginBottom: 2 }}>Recipient</p><p style={{ color: 'var(--text-primary)' }}>{log.candidate?.fullName || log.toName || '—'}</p></div>
          {log.requirement && <div><p style={{ color: 'var(--text-muted)', marginBottom: 2 }}>Requirement</p><p style={{ color: 'var(--text-primary)' }}>{log.requirement.jobTitle}</p></div>}
          <div><p style={{ color: 'var(--text-muted)', marginBottom: 2 }}>Sent by</p><p style={{ color: 'var(--text-primary)' }}>{log.sentBy?.name}</p></div>
          <div><p style={{ color: 'var(--text-muted)', marginBottom: 2 }}>Date</p><p style={{ color: 'var(--text-primary)' }}>{new Date(log.createdAt).toLocaleString()}</p></div>
          {log.template && <div><p style={{ color: 'var(--text-muted)', marginBottom: 2 }}>Template</p><Badge category={log.template.category} /></div>}
          <div>
            <p style={{ color: 'var(--text-muted)', marginBottom: 2 }}>Status</p>
            <span style={{ ...statusBadgeStyle(log.status, isDark), fontSize: 11, padding: '2px 8px', borderRadius: 9999, fontWeight: 500 }}>{log.status}</span>
          </div>
          <div>
            <p style={{ color: 'var(--text-muted)', marginBottom: 2 }}>Type</p>
            <span style={log.emailType === 'client'
              ? { background: isDark ? 'rgba(16,185,129,0.15)' : '#ecfdf5', color: isDark ? '#6ee7b7' : '#047857', border: '1px solid ' + (isDark ? 'rgba(16,185,129,0.3)' : '#a7f3d0'), fontSize: 11, padding: '2px 8px', borderRadius: 9999, fontWeight: 500 }
              : { background: isDark ? 'rgba(99,102,241,0.15)' : '#eef2ff', color: isDark ? '#a5b4fc' : '#4338ca', border: '1px solid ' + (isDark ? 'rgba(99,102,241,0.3)' : '#c7d2fe'), fontSize: 11, padding: '2px 8px', borderRadius: 9999, fontWeight: 500 }}>
              {log.emailType === 'client' ? '🏢 Client' : '✉ Candidate'}
            </span>
          </div>
        </div>
        <div><p style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 4 }}>Subject</p><p style={{ color: 'var(--text-primary)', background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 8, padding: '8px 12px' }} className="text-sm">{log.subject}</p></div>
        <div>
          <p style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 4 }}>Body</p>
          <div style={{ background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 8, padding: '12px' }} className="max-h-72 overflow-y-auto">
            {log.body == null
              ? <p style={{ color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic' }}>Email body is not available for your role.</p>
              : log.body?.startsWith('<') || log.body?.includes('<table')
                // Sanitized before injecting — a crafted value in a logged email must
                // not become stored XSS for staff viewing the log. Default DOMPurify
                // keeps tables/inline styles but strips scripts and event handlers.
                ? <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(String(log.body)) }} style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.6 }} />
                : <pre style={{ color: 'var(--text-secondary)' }} className="text-sm whitespace-pre-wrap font-sans leading-relaxed">{log.body}</pre>}
          </div>
        </div>
        {log.errorMessage && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3"><p className="text-red-400 text-xs">{log.errorMessage}</p></div>}
      </div>
    </Modal>
  );
}

const EMPTY_LOG_FILTERS = { candidateId: '', requirementId: '', clientId: '', sentById: '', dateFrom: '', dateTo: '' };

function LogsPanel({ emailType, filterOpts, showSentBy }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [viewLog, setViewLog] = useState(null);
  // Kept for this tab — survives leaving the page and coming back.
  const [filters, setFilters] = usePersistentState('emails:logs:filters', EMPTY_LOG_FILTERS);
  const LIMIT = 15;

  const { requirements = [], clients = [], senders = [], candidates = [] } = filterOpts || {};
  const hasFilters = Object.values(filters).some(Boolean);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { emailType, page, limit: LIMIT };
      if (filters.candidateId)   params.candidateId   = filters.candidateId;
      if (filters.requirementId) params.requirementId = filters.requirementId;
      if (filters.clientId)      params.clientId      = filters.clientId;
      if (filters.sentById)      params.sentById      = filters.sentById;
      if (filters.dateFrom)      params.dateFrom      = filters.dateFrom;
      if (filters.dateTo)        params.dateTo        = filters.dateTo;
      const { data } = await emailAPI.getLogs(params);
      setLogs(data.logs); setTotal(data.total);
    } catch { toast.error('Failed to load logs.'); }
    finally { setLoading(false); }
  }, [emailType, page, filters]);

  useEffect(() => { load(); }, [load]);

  // Apply a filter change and jump back to page 1 (same render → single fetch).
  const setFilter = (patch) => { setFilters(f => ({ ...f, ...patch })); setPage(1); };
  const clearFilters = () => { setFilters(EMPTY_LOG_FILTERS); setPage(1); };

  // Requirement options narrow to the chosen client (if any).
  const reqOptions = requirements
    .filter(r => !filters.clientId || r.clientId === filters.clientId)
    .map(r => ({ value: r.id, label: r.jobTitle, sub: r.client?.clientName || '' }));

  const dateInputStyle = {
    background: 'var(--bg-input)', border: '1px solid var(--border-input)',
    color: 'var(--text-primary)', borderRadius: 8, padding: '8px 10px', fontSize: 13,
  };

  const isClient = emailType === 'client';

  return (
    <div>
      {/* Filter bar — candidate partition filters by candidate, client partition by client */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        {isClient ? (
          <div style={{ minWidth: 140 }} className="flex-1 sm:flex-none">
            <SearchableSelect
              label="Client"
              value={filters.clientId}
              onChange={v => setFilter({ clientId: v, requirementId: '' })}
              placeholder="All clients"
              options={clients.map(c => ({ value: c.id, label: c.clientName, sub: c.industry || '' }))}
            />
          </div>
        ) : (
          <div style={{ minWidth: 140 }} className="flex-1 sm:flex-none">
            <SearchableSelect
              label="Candidate"
              value={filters.candidateId}
              onChange={v => setFilter({ candidateId: v })}
              placeholder="All candidates"
              options={candidates.map(c => ({ value: c.id, label: c.fullName, sub: c.email || '' }))}
            />
          </div>
        )}
        <div style={{ minWidth: 140 }} className="flex-1 sm:flex-none">
          <SearchableSelect
            label="Requirement"
            value={filters.requirementId}
            onChange={v => setFilter({ requirementId: v })}
            placeholder="All requirements"
            options={reqOptions}
          />
        </div>
        {showSentBy && (
          <div style={{ minWidth: 140 }} className="flex-1 sm:flex-none">
            <SearchableSelect
              label="Sent by"
              value={filters.sentById}
              onChange={v => setFilter({ sentById: v })}
              placeholder="Anyone"
              options={senders.map(u => ({ value: u.id, label: u.name, sub: u.email || '' }))}
            />
          </div>
        )}
        <div>
          <label style={{ color: 'var(--text-muted)', fontSize: 12, display: 'block', marginBottom: 4 }}>From</label>
          <input type="date" value={filters.dateFrom} max={filters.dateTo || undefined}
            onChange={e => setFilter({ dateFrom: e.target.value })} style={dateInputStyle} />
        </div>
        <div>
          <label style={{ color: 'var(--text-muted)', fontSize: 12, display: 'block', marginBottom: 4 }}>To</label>
          <input type="date" value={filters.dateTo} min={filters.dateFrom || undefined}
            onChange={e => setFilter({ dateTo: e.target.value })} style={dateInputStyle} />
        </div>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>✕ Clear</Button>
        )}
      </div>

      {/* Summary bar */}
      <div className="flex items-center justify-between mb-4">
        <p style={{ color: 'var(--text-secondary)' }} className="text-sm">
          <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{total}</span> {isClient ? 'client emails' : 'candidate emails'}{hasFilters ? ' match' : ' sent'}
        </p>
        <Button variant="ghost" size="xs" onClick={load}>↻ Refresh</Button>
      </div>

      {loading
        ? <div style={{ color: 'var(--text-muted)' }} className="text-center py-16 text-sm">Loading…</div>
        : logs.length === 0
          ? (
            <div className="text-center py-16">
              <p style={{ color: 'var(--text-muted)' }} className="text-sm">
                {hasFilters
                  ? 'No emails match these filters.'
                  : isClient ? 'No client emails sent yet.' : 'No candidate emails sent yet.'}
              </p>
            </div>
          )
          : (
            <>
              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)' }} className="rounded-xl overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-raised)' }}>
                      {(isClient
                        ? ['Client / Contact', 'Requirement', 'Subject', 'Template', 'Sent by', 'Date', 'Status', '']
                        : ['Candidate', 'Subject', 'Template', 'Sent by', 'Date', 'Status', '']
                      ).map(h => (
                        <th key={h} style={{ color: 'var(--text-muted)', textAlign: 'left', fontSize: 11, fontWeight: 500 }} className="px-4 py-3 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(log => (
                      <tr key={log.id} style={{ borderBottom: '1px solid var(--border-subtle)' }} className="transition-colors">
                        {/* Recipient cell */}
                        <td className="px-4 py-3">
                          <p style={{ color: 'var(--text-primary)' }} className="text-xs font-medium">{log.candidate?.fullName || log.toName || '—'}</p>
                          <p style={{ color: 'var(--text-muted)' }} className="text-xs">{log.toEmail}</p>
                        </td>
                        {/* Requirement column — client only */}
                        {isClient && (
                          <td style={{ color: 'var(--text-secondary)' }} className="px-4 py-3 text-xs">
                            {log.requirement?.jobTitle || <span style={{ color: 'var(--text-muted)' }}>—</span>}
                          </td>
                        )}
                        {/* Subject */}
                        <td style={{ color: 'var(--text-secondary)' }} className="px-4 py-3 text-xs max-w-[200px]">
                          <span className="line-clamp-2 block">{log.subject}</span>
                        </td>
                        {/* Template */}
                        <td className="px-4 py-3">
                          {log.template ? <Badge category={log.template.category} /> : <span style={{ color: 'var(--text-muted)' }} className="text-xs">—</span>}
                        </td>
                        {/* Sent by */}
                        <td style={{ color: 'var(--text-secondary)' }} className="px-4 py-3 text-xs">{log.sentBy?.name}</td>
                        {/* Date */}
                        <td style={{ color: 'var(--text-secondary)' }} className="px-4 py-3 text-xs whitespace-nowrap">
                          {new Date(log.createdAt).toLocaleDateString()}<br />
                          <span style={{ color: 'var(--text-muted)' }}>{new Date(log.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </td>
                        {/* Status */}
                        <td className="px-4 py-3">
                          <span style={{ ...statusBadgeStyle(log.status, isDark), fontSize: 11, padding: '2px 8px', borderRadius: 9999, fontWeight: 500 }}>{log.status}</span>
                        </td>
                        {/* View */}
                        <td className="px-4 py-3">
                          <Button variant="ghost" size="xs" onClick={() => setViewLog(log)}>View</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {total > LIMIT && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  <Button variant="ghost" size="xs" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>← Prev</Button>
                  <span style={{ color: 'var(--text-muted)' }} className="text-xs">Page {page} of {Math.ceil(total / LIMIT)}</span>
                  <Button variant="ghost" size="xs" onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(total / LIMIT)}>Next →</Button>
                </div>
              )}
            </>
          )
      }
      <LogViewModal log={viewLog} onClose={() => setViewLog(null)} />
    </div>
  );
}

// ── Sent Logs Tab (two inner partitions) ──────────────────────────────────────
function LogsTab() {
  const { isRecruiter } = useAuth();
  const recruiter = isRecruiter();
  const [partition, setPartition] = useState('candidate');

  // Filter option lists — loaded once and shared by both partitions. Uses
  // allSettled so one endpoint failing (e.g. a role without client access)
  // doesn't blank out the other filters.
  const [filterOpts, setFilterOpts] = useState({ requirements: [], clients: [], senders: [], candidates: [] });
  useEffect(() => {
    (async () => {
      const [r, c, s, cand] = await Promise.allSettled([
        requirementAPI.dropdown(),
        clientAPI.dropdown(),
        recruiter ? Promise.resolve(null) : emailAPI.getLogSenders(),
        candidateAPI.dropdown(),
      ]);
      setFilterOpts({
        requirements: r.status === 'fulfilled' ? (r.value?.data?.requirements || []) : [],
        clients:      c.status === 'fulfilled' ? (c.value?.data?.clients || [])      : [],
        senders:      s.status === 'fulfilled' && s.value ? (s.value.data?.senders || []) : [],
        candidates:   cand.status === 'fulfilled' ? (cand.value?.data?.candidates || []) : [],
      });
    })();
  }, [recruiter]);

  // Recruiters only see candidate logs — no partition switcher needed
  if (recruiter) {
    return (
      <div>
        <div className="flex items-center gap-2 mb-5 bg-indigo-500/10 border border-indigo-500/30 rounded-xl px-4 py-3">
          <span className="text-indigo-400 text-sm">✉</span>
          <p className="text-indigo-300 text-sm font-medium">Your sent candidate emails</p>
        </div>
        <div className="h-0.5 rounded-full mb-5 bg-indigo-600" />
        <LogsPanel emailType="candidate" filterOpts={filterOpts} showSentBy={false} />
      </div>
    );
  }

  return (
    <div>
      {/* Partition switcher */}
      <div style={{ background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: 4, display: 'flex', flexWrap: 'wrap', marginBottom: 24 }}>
        <Button onClick={() => setPartition('candidate')} variant={partition === 'candidate' ? 'primary' : 'ghost'} size="sm">✉ Sent to Candidates</Button>
        <Button onClick={() => setPartition('client')} variant={partition === 'client' ? 'success' : 'ghost'} size="sm">🏢 Sent to Clients</Button>
      </div>

      {/* Coloured top-border accent strip */}
      <div className={`h-0.5 rounded-full mb-5 ${partition === 'candidate' ? 'bg-indigo-600' : 'bg-emerald-600'}`} />

      {partition === 'candidate' && <LogsPanel emailType="candidate" filterOpts={filterOpts} showSentBy={true} />}
      {partition === 'client'    && <LogsPanel emailType="client" filterOpts={filterOpts} showSentBy={true} />}
    </div>
  );
}

// ── Main EmailDashboard ───────────────────────────────────────────────────────
export default function EmailDashboard() {
  const location           = useLocation();
  const { isRecruiter }    = useAuth();
  const recruiter          = isRecruiter();
  const prefill            = location.state || null;   // { compose: 'client', candidate: {...} }

  // If we arrived via the "Email to Client" shortcut, open Compose tab by default
  // The last tab is kept for this tab; arriving with a compose prefill (e.g.
  // "Email Client" from Submissions) always opens Compose.
  const [activeTab, setActiveTab] = usePersistentState('emails:tab', 'Templates', prefill?.compose ? 'Compose' : undefined);

  return (
    <div className="space-y-6">
      <div>
        <h1 style={{ color: 'var(--text-primary)' }} className="text-xl sm:text-2xl font-bold">Email Dashboard</h1>
        <p style={{ color: 'var(--text-secondary)' }} className="text-sm mt-1">
          {recruiter
            ? 'Send emails to candidates and view your sent history'
            : 'Manage templates and send emails to candidates and clients'}
        </p>
      </div>
      <div style={{ background: 'var(--bg-raised)', borderRadius: 12, padding: 4, display: 'flex', flexWrap: 'wrap' }}>
        {TABS.map(tab => (
          <Button key={tab} size="sm" variant={activeTab === tab ? 'primary' : 'ghost'} onClick={() => setActiveTab(tab)}>{tab}</Button>
        ))}
      </div>
      <div>
        {activeTab === 'Templates' && <TemplatesTab />}
        {activeTab === 'Compose'   && <ComposeTab prefill={prefill} />}
        {activeTab === 'Sent Logs' && <LogsTab />}
      </div>
    </div>
  );
}
